import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { supabase } from '../config/supabase';
import advancedEmailAutomationService from '../services/advancedEmailAutomationService';
import { runMigration } from '../utils/migrateExistingLeads';
import { useRBAC } from '../contexts/RBACContext';
import logger from '../utils/logger';

import {
  Play,
  Pause,
  Settings,
  Calendar,
  Mail,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Clock,
  Target,
  Zap,
  RefreshCw,
  Edit,
  Save,
  X,
  Eye,
  Plus,
  Minus,
  Globe,
  Users,
  Monitor,
  Smartphone,
  Tablet,
  Phone,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Filter,
  Search,
  Download,
  Upload,
  Trash2,
  Copy,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Activity,
  Layers,
  List,
  Grid,
  MoreHorizontal,
  ChevronRight,
  ChevronDown,
  Bell,
  Shield,
  Repeat,
  Timer,
  Inbox,
  Send,
  Code
} from 'lucide-react';

const AdvancedEmailAutomationControl = () => {
  const { canDelete, assignedCampaigns, isAdminOrHigher, hasPermission } = useRBAC();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  
  // Campaign Management
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [campaignActivations, setCampaignActivations] = useState([]);
  
  // Sequences
  const [sequences, setSequences] = useState([]);
  const [stoppedSequences, setStoppedSequences] = useState([]);
  const [sequenceAnalytics, setSequenceAnalytics] = useState(null);
  const [recentSentSequences, setRecentSentSequences] = useState([]);
  
  // Stopped Sequences UI State
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [groupedStoppedSequences, setGroupedStoppedSequences] = useState({});
  
  // Follow-up Calls
  const [followUpCalls, setFollowUpCalls] = useState([]);
  const [deletingLead, setDeletingLead] = useState(null);
  const [campaignsWithCalls, setCampaignsWithCalls] = useState([]);
  const [selectedCampaignForDisplay, setSelectedCampaignForDisplay] = useState('');
  const [currentCampaignPage, setCurrentCampaignPage] = useState(1);
  const [currentLeadsPage, setCurrentLeadsPage] = useState(1);
  const campaignsPerPage = 10;
  const leadsPerPage = 10;

  // Email Opens
  const [emailOpens, setEmailOpens] = useState([]);
  const [totalEmailOpens, setTotalEmailOpens] = useState(0);
  const [totalUniqueLeads, setTotalUniqueLeads] = useState(0);
  const [totalMultipleOpens, setTotalMultipleOpens] = useState(0);
  const [opensLoading, setOpensLoading] = useState(false);
  
  // Email Opens Pagination
  const [currentEmailOpensPage, setCurrentEmailOpensPage] = useState(1);
  const emailOpensPerPage = 10;
  const [opensStepFilter, setOpensStepFilter] = useState('all');
  const [opensSearchQuery, setOpensSearchQuery] = useState('');
  const [opensSearchActive, setOpensSearchActive] = useState(false);
  
  // Active Sequences Pagination and Filtering
  const [currentSequencePage, setCurrentSequencePage] = useState(1);
  const [sequenceStepFilter, setSequenceStepFilter] = useState('all');
  const [totalSequences, setTotalSequences] = useState(0);
  const sequencesPerPage = 10;
  const [sequenceSearchQuery, setSequenceSearchQuery] = useState('');
  const [sequenceCampaignFilter, setSequenceCampaignFilter] = useState('');
  
  // Migration
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  
  // Overdue Leads
  const [overdueSequences, setOverdueSequences] = useState([]);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [reschedulingOverdue, setReschedulingOverdue] = useState(false);
  
  // Actual sent counts from execution table (sequence_id -> count)
  const [actualSentCounts, setActualSentCounts] = useState({});
  
  // Campaign config modal: overdue & stopped per step
  const [campaignStepData, setCampaignStepData] = useState(null);
  const [campaignStepLoading, setCampaignStepLoading] = useState(false);
  const [reschedulingCampaign, setReschedulingCampaign] = useState(false);
  const [stepRescheduleSettings, setStepRescheduleSettings] = useState({});
  const [reschedulingStep, setReschedulingStep] = useState(null);
  
  // Today's Pending Executions
  const [todaysPending, setTodaysPending] = useState([]);
  const [todaysPendingTotal, setTodaysPendingTotal] = useState(0);
  const [todaysPendingPage, setTodaysPendingPage] = useState(1);
  const [todaysPendingLoading, setTodaysPendingLoading] = useState(false);
  const todaysPendingPerPage = 10;

  // Execution Search
  const [executionSearchQuery, setExecutionSearchQuery] = useState('');
  const [executionSearchResults, setExecutionSearchResults] = useState([]);
  const [executionSearchTotal, setExecutionSearchTotal] = useState(0);
  const [executionSearchPage, setExecutionSearchPage] = useState(1);
  const [executionSearchLoading, setExecutionSearchLoading] = useState(false);
  const [executionSearchActive, setExecutionSearchActive] = useState(false);
  const executionSearchPerPage = 10;

  // Activation History

  const [selectedCampaignForCalls, setSelectedCampaignForCalls] = useState('');
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    trigger_event: 'open',
    subject_template: '',
    content_template: '',
    delay_minutes: 10080, // 7 days in minutes
    is_active: true,
    is_sequence_template: false,
    sequence_step: 1,
    sequence_config: {},
    follow_up_subject: '',
    follow_up_content: ''
  });
  
  // Modals and UI State
  const [showCampaignActivationModal, setShowCampaignActivationModal] = useState(false);
  const [showSequenceConfigModal, setShowSequenceConfigModal] = useState(false);
  const [showTimeWindowModal, setShowTimeWindowModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // New Campaign Activation Form
  const [newActivation, setNewActivation] = useState({
    campaign_id: '',
    campaign_name: '',
    template_id: '',
    sequence_config: {
      steps: [
        { step: 1, delay_days: 7, template_id: null },
        { step: 2, delay_days: 15, template_id: null },
        { step: 3, delay_days: 15, template_id: null },
        { step: 4, delay_days: 15, template_id: null },
        { step: 5, delay_days: 15, template_id: null }
      ],
      stop_on_reply: true,
      max_sequences: 5
    },
    sending_time_window: {
      start_time: '01:00',
      end_time: '09:00',
      timezone: 'Europe/Helsinki',
      randomize_within_window: true,
      min_delay_minutes: 1,
      max_delay_minutes: 10
    }
  });

  // Campaign filtering helper: checks if a campaign_name belongs to user's assigned campaigns
  // Returns true for admin/developer (no filtering), false if no campaigns assigned
  const isCampaignAllowed = (campaignName) => {
    if (isAdminOrHigher()) return true;
    if (!assignedCampaigns || assignedCampaigns.length === 0) return false;
    return assignedCampaigns.includes(campaignName);
  };

  // Filter an array of items by campaign_name field
  // Accepts a getter function to extract campaign_name from each item
  const filterByCampaignName = (items, getCampaignName = (item) => item.campaign_name) => {
    if (!Array.isArray(items)) return items;
    if (isAdminOrHigher()) return items;
    if (!assignedCampaigns || assignedCampaigns.length === 0) return [];
    return items.filter(item => {
      const name = getCampaignName(item);
      return name && assignedCampaigns.includes(name);
    });
  };

  // Determine which tabs the current user can see
  const allTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'campaigns', label: 'Campaign Activation', icon: Target },
    { id: 'sequences', label: 'Follow-up Sequences', icon: Layers },
    { id: 'opens', label: 'Email Opens', icon: Eye },
    { id: 'followup-calls', label: 'Follow-up Calls', icon: Phone },
    { id: 'templates', label: 'Templates', icon: Mail },
    { id: 'todays-pending', label: "Today's Pending", icon: Timer },
    { id: 'settings', label: 'Advanced Settings', icon: Settings }
  ];

  // Moderators/users only see these tabs (read-only data tabs)
  // Follow-up Calls hidden for moderator/user - only admin/developer can see it
  const moderatorTabIds = ['overview', 'sequences', 'opens', 'todays-pending'];
  const visibleTabs = isAdminOrHigher()
    ? allTabs
    : allTabs.filter(tab => moderatorTabIds.includes(tab.id));

  useEffect(() => {
    initializeAdvancedAutomation();
    
    // Create single interval with proper cleanup
    const interval = setInterval(loadAllData, 30000); // Refresh every 30 seconds
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []); // Empty dependency array to prevent re-creation

  // Reload email opens when page changes
  useEffect(() => {
    if (!loading) {
      loadEmailOpens(currentEmailOpensPage, opensStepFilter, opensSearchQuery);
    }
  }, [currentEmailOpensPage]);

  // Reload email opens when step filter changes (reset to page 1)
  useEffect(() => {
    if (!loading) {
      setCurrentEmailOpensPage(1);
      loadEmailOpens(1, opensStepFilter, opensSearchQuery);
    }
  }, [opensStepFilter]);

  // Load email opens when tab is activated or campaignActivations become available
  useEffect(() => {
    if (activeTab === 'opens' && !loading) {
      loadEmailOpens(1, opensStepFilter, opensSearchQuery);
    }
  }, [activeTab, campaignActivations]);

  // Load today's pending when tab is activated
  useEffect(() => {
    if (activeTab === 'todays-pending' && !loading) {
      loadTodaysPending(1);
    }
  }, [activeTab]);

  const initializeAdvancedAutomation = async () => {
    try {
      await advancedEmailAutomationService.initialize();
      await loadAllData();
    } catch (error) {
      logger.error('Error initializing advanced automation:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    try {
      // Load data with individual error handling to prevent one failure from blocking others
      const results = await Promise.allSettled([
        loadSettings(),
        loadCampaigns(),
        loadCampaignActivations(),
        loadSequences(currentSequencePage, sequenceStepFilter),
        loadStoppedSequences(),
        loadFollowUpCalls(),
        loadTemplates(),
        loadSequenceAnalytics(),
        loadEmailOpens(),
        loadRecentSentSequences(),
        loadOverdueSequences()
      ]);
      
      // Log any failures but continue operation
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const operations = ['Settings', 'Campaigns', 'Campaign Activations', 'Sequences', 'Stopped Sequences', 'Follow-up Calls', 'Templates', 'Analytics', 'Email Opens', 'Recent Sent Sequences', 'Overdue Sequences'];
          logger.warn(`Failed to load ${operations[index]}:`, result.reason);
        }
      });
    } catch (error) {
      logger.error('Error loading data:', error);
    }
  };

  const loadOverdueSequences = async () => {
    try {
      setOverdueLoading(true);
      const rawData = await advancedEmailAutomationService.getOverdueSequences();
      // Filter by assigned campaigns (RPC is SECURITY DEFINER, bypasses RLS)
      const data = filterByCampaignName(rawData || [], (seq) =>
        seq.campaign_name || seq.email_automation_campaign_activation?.campaign_name
      );
      setOverdueSequences(data);
    } catch (error) {
      logger.error('Error loading overdue sequences:', error);
      setOverdueSequences([]);
    } finally {
      setOverdueLoading(false);
    }
  };

  const handleRescheduleOverdue = async (campaignId = null) => {
    try {
      setReschedulingOverdue(true);
      const result = await advancedEmailAutomationService.rescheduleOverdueSequences(campaignId);
      if (result?.rescheduled > 0) {
        alert(`Successfully rescheduled ${result.rescheduled} overdue sequences!`);
      } else {
        alert('No overdue sequences found to reschedule.');
      }
      await loadOverdueSequences();
    } catch (error) {
      logger.error('Error rescheduling overdue:', error);
      alert('Error rescheduling overdue sequences: ' + error.message);
    } finally {
      setReschedulingOverdue(false);
    }
  };

  const loadActualSentCounts = async (seqIds) => {
    try {
      if (!seqIds || seqIds.length === 0) return;
      const counts = await advancedEmailAutomationService.getActualSentCounts(seqIds);
      setActualSentCounts(prev => ({ ...prev, ...counts }));
    } catch (error) {
      logger.error('Error loading actual sent counts:', error);
    }
  };

  const loadCampaignStepData = async (campaignId) => {
    try {
      setCampaignStepLoading(true);
      const data = await advancedEmailAutomationService.getCampaignSequencesByStep(campaignId);
      setCampaignStepData(data);
    } catch (error) {
      logger.error('Error loading campaign step data:', error);
      setCampaignStepData(null);
    } finally {
      setCampaignStepLoading(false);
    }
  };

  const handleRescheduleCampaign = async (campaignId, type = 'all') => {
    try {
      setReschedulingCampaign(true);
      const result = await advancedEmailAutomationService.rescheduleCampaignSequences(campaignId, type);
      if (result?.rescheduled > 0) {
        alert(`Successfully rescheduled ${result.rescheduled} sequences (${type})!`);
      } else {
        alert(result?.error || 'No sequences found to reschedule.');
      }
      // Refresh data
      await loadCampaignStepData(campaignId);
      await loadOverdueSequences();
      await loadStoppedSequences();
      await loadSequenceAnalytics();
    } catch (error) {
      logger.error('Error rescheduling campaign sequences:', error);
      alert('Error rescheduling: ' + error.message);
    } finally {
      setReschedulingCampaign(false);
    }
  };

  const handleRescheduleStep = async (campaignId, step, type = 'all') => {
    try {
      setReschedulingStep(step);
      const settings = stepRescheduleSettings[step] || {};
      const options = { type };

      if (settings.scheduledDate && settings.scheduledTime) {
        options.scheduledDateTime = new Date(`${settings.scheduledDate}T${settings.scheduledTime}`).toISOString();
      }
      if (settings.templateId) {
        options.templateId = settings.templateId;
      }

      const result = await advancedEmailAutomationService.rescheduleCampaignStep(campaignId, step, options);
      if (result?.rescheduled > 0) {
        alert(`Step ${step}: Rescheduled ${result.rescheduled} sequences (${type})!`);
      } else {
        alert(result?.error || `Step ${step}: No sequences found to reschedule.`);
      }
      await loadCampaignStepData(campaignId);
      await loadOverdueSequences();
      await loadStoppedSequences();
      await loadSequenceAnalytics();
    } catch (error) {
      logger.error('Error rescheduling step:', error);
      alert('Error rescheduling step: ' + error.message);
    } finally {
      setReschedulingStep(null);
    }
  };

  const updateStepSetting = (step, key, value) => {
    setStepRescheduleSettings(prev => ({
      ...prev,
      [step]: { ...(prev[step] || {}), [key]: value }
    }));
  };

  const loadTodaysPending = async (page = 1) => {
    try {
      setTodaysPendingLoading(true);
      const offset = (page - 1) * todaysPendingPerPage;

      // Get today's date boundaries in UTC
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();

      // For non-admin users: first get allowed sequence IDs based on campaign_name
      let allowedSequenceIds = null;
      if (!isAdminOrHigher() && assignedCampaigns && assignedCampaigns.length > 0) {
        const { data: seqData } = await supabase
          .from('email_automation_sequences')
          .select('id')
          .in('campaign_name', assignedCampaigns);
        allowedSequenceIds = (seqData || []).map(s => s.id);
        if (allowedSequenceIds.length === 0) {
          setTodaysPending([]);
          setTodaysPendingTotal(0);
          setTodaysPendingPage(page);
          setTodaysPendingLoading(false);
          return;
        }
      } else if (!isAdminOrHigher()) {
        setTodaysPending([]);
        setTodaysPendingTotal(0);
        setTodaysPendingPage(page);
        setTodaysPendingLoading(false);
        return;
      }

      let query = supabase
        .from('email_automation_executions')
        .select(`
          *,
          email_automation_sequences(campaign_id, current_step, max_steps, status,
            email_automation_campaign_activation(campaign_name)
          )
        `, { count: 'exact' })
        .eq('status', 'pending')
        .gte('scheduled_send_time', todayStart)
        .lte('scheduled_send_time', todayEnd)
        .order('scheduled_send_time', { ascending: true });

      // Apply campaign filter at query level for non-admin
      if (allowedSequenceIds) {
        query = query.in('sequence_id', allowedSequenceIds);
      }

      query = query.range(offset, offset + todaysPendingPerPage - 1);

      const { data, error, count } = await query;

      if (error) {
        logger.error('Error loading today\'s pending executions:', error);
        setTodaysPending([]);
        setTodaysPendingTotal(0);
        return;
      }

      setTodaysPending(data || []);
      setTodaysPendingTotal(count || 0);
      setTodaysPendingPage(page);
    } catch (error) {
      logger.error('Error in loadTodaysPending:', error);
      setTodaysPending([]);
      setTodaysPendingTotal(0);
    } finally {
      setTodaysPendingLoading(false);
    }
  };

  const searchExecutions = async (query = executionSearchQuery, page = 1) => {
    try {
      setExecutionSearchLoading(true);
      setExecutionSearchActive(true);
      const offset = (page - 1) * executionSearchPerPage;
      const trimmed = query.trim();

      if (!trimmed) {
        setExecutionSearchResults([]);
        setExecutionSearchTotal(0);
        setExecutionSearchActive(false);
        setExecutionSearchLoading(false);
        return;
      }

      const { data, error, count } = await supabase
        .from('email_automation_executions')
        .select(`
          *,
          email_automation_sequences(campaign_id, current_step, max_steps, status,
            email_automation_campaign_activation(campaign_name)
          )
        `, { count: 'exact' })
        .ilike('lead_email', `%${trimmed}%`)
        .order('created_at', { ascending: false })
        .range(offset, offset + executionSearchPerPage - 1);

      if (error) {
        logger.error('Error searching executions:', error);
        setExecutionSearchResults([]);
        setExecutionSearchTotal(0);
        return;
      }

      // Filter by assigned campaigns for non-admin users
      const filteredExecs = filterByCampaignName(data || [], (item) =>
        item.email_automation_sequences?.email_automation_campaign_activation?.campaign_name
      );
      setExecutionSearchResults(filteredExecs);
      setExecutionSearchTotal(isAdminOrHigher() ? (count || 0) : filteredExecs.length);
      setExecutionSearchPage(page);
    } catch (error) {
      logger.error('Error in searchExecutions:', error);
      setExecutionSearchResults([]);
      setExecutionSearchTotal(0);
    } finally {
      setExecutionSearchLoading(false);
    }
  };

  const clearExecutionSearch = () => {
    setExecutionSearchQuery('');
    setExecutionSearchResults([]);
    setExecutionSearchTotal(0);
    setExecutionSearchPage(1);
    setExecutionSearchActive(false);
  };

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('email_automation_settings')
        .select('*')
        .single();
      
      if (error && error.code !== 'PGRST116') {
        logger.error('Error loading settings:', error);
        // Set default settings if loading fails
        setSettings({
          is_enabled: true,
          max_daily_sends: 100,
          current_daily_sends: 0,
          monitoring_enabled: true
        });
        return;
      }
      
      // Get ACTUAL sent count for today from email_automation_executions
      // This ensures consistency with Dashboard chart which uses the same source
      const today = new Date().toISOString().split('T')[0];
      const { count: actualSentToday } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'sent')
        .gte('executed_at', `${today}T00:00:00`)
        .lte('executed_at', `${today}T23:59:59`);
      
      // Use actual count from executions table as source of truth
      setSettings({
        ...(data || {
          is_enabled: true,
          max_daily_sends: 100,
          monitoring_enabled: true
        }),
        current_daily_sends: actualSentToday || 0
      });
    } catch (error) {
      logger.error('Error in loadSettings:', error);
      // Set default settings on any error
      setSettings({
        is_enabled: true,
        max_daily_sends: 100,
        current_daily_sends: 0,
        monitoring_enabled: true
      });
    }
  };

  const loadCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('campaign_performance')
        .select('*')
        .order('campaign_name');
      
      if (error) {
        logger.error('Error loading campaigns:', error);
        setCampaigns([]);
        return;
      }
      
      // Filter by assigned campaigns for non-admin users
      const filtered = filterByCampaignName(data || []);
      setCampaigns(filtered);
    } catch (error) {
      logger.error('Error in loadCampaigns:', error);
      setCampaigns([]);
    }
  };

  const loadCampaignActivations = async () => {
    try {
      const { data, error } = await supabase
        .from('email_automation_campaign_activation')
        .select(`
          *,
          email_automation_templates(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error('Error loading campaign activations:', error);
        setCampaignActivations([]);
        setActiveCampaigns([]);
        return;
      }
      
      // Filter by assigned campaigns for non-admin users
      const filtered = filterByCampaignName(data || []);
      setCampaignActivations(filtered);
      setActiveCampaigns(filtered.filter(ca => ca.is_active));
    } catch (error) {
      logger.error('Error in loadCampaignActivations:', error);
      setCampaignActivations([]);
      setActiveCampaigns([]);
    }
  };

  const loadSequences = async (page = 1, stepFilter = 'all', search = sequenceSearchQuery, campaign = sequenceCampaignFilter) => {
    try {
      const offset = (page - 1) * sequencesPerPage;
      
      // Build query with step filtering
      let query = supabase
        .from('email_automation_sequences')
        .select(`
          *,
          email_automation_campaign_activation(campaign_name, sequence_config)
        `, { count: 'exact' })
        .order('created_at', { ascending: false });
      
      // Apply step filter
      if (stepFilter === 'completed') {
        // Completed filter: show ONLY status='completed' (progression Done 5/5)
        query = query.eq('status', 'completed');
      } else if (stepFilter !== 'all') {
        // Step 1-5 filter: show ONLY status='active' AND on that exact step
        query = query.eq('status', 'active')
                     .eq('current_step', parseInt(stepFilter));
      } else {
        // All Steps: show ONLY status='active' sequences (most recent first)
        query = query.eq('status', 'active');
      }
      
      // Apply lead email search
      if (search && search.trim()) {
        query = query.ilike('lead_email', `%${search.trim()}%`);
      }
      
      // Apply campaign filter (user-selected dropdown)
      if (campaign) {
        query = query.eq('campaign_id', campaign);
      }
      
      // RBAC: Filter by assigned campaigns for non-admin users
      if (!isAdminOrHigher() && assignedCampaigns && assignedCampaigns.length > 0) {
        query = query.in('campaign_name', assignedCampaigns);
      } else if (!isAdminOrHigher() && (!assignedCampaigns || assignedCampaigns.length === 0)) {
        // No campaigns assigned: return empty
        setSequences([]);
        setTotalSequences(0);
        return;
      }
      
      // Apply pagination
      query = query.range(offset, offset + sequencesPerPage - 1);
      
      const { data, error, count } = await query;
      
      if (error) {
        logger.error('Error loading sequences:', error);
        setSequences([]);
        setTotalSequences(0);
        return;
      }
      
      setSequences(data || []);
      setTotalSequences(count || 0);
      setCurrentSequencePage(page);
      setSequenceStepFilter(stepFilter);
      
      // Load actual sent counts for these sequences from execution table
      if (data && data.length > 0) {
        const seqIds = data.map(s => s.id);
        loadActualSentCounts(seqIds);
      }
    } catch (error) {
      logger.error('Error in loadSequences:', error);
      setSequences([]);
      setTotalSequences(0);
    }
  };

  const loadStoppedSequences = async () => {
    try {
      const rawStoppedSeqs = await advancedEmailAutomationService.getStoppedSequences();
      // Filter by assigned campaigns for non-admin users
      const stoppedSeqs = filterByCampaignName(rawStoppedSeqs, (seq) =>
        seq.email_automation_campaign_activation?.campaign_name
      );
      setStoppedSequences(stoppedSeqs);
      
      // Group sequences by campaign
      const grouped = stoppedSeqs.reduce((acc, sequence) => {
        const campaignName = sequence.email_automation_campaign_activation?.campaign_name || sequence.campaign_id || 'Unknown Campaign';
        const campaignId = sequence.campaign_id || 'unknown';
        
        if (!acc[campaignId]) {
          acc[campaignId] = {
            name: campaignName,
            sequences: []
          };
        }
        acc[campaignId].sequences.push(sequence);
        return acc;
      }, {});
      
      setGroupedStoppedSequences(grouped);
      
      // Load actual sent counts for stopped sequences too
      if (stoppedSeqs && stoppedSeqs.length > 0) {
        const stoppedSeqIds = stoppedSeqs.map(s => s.id);
        loadActualSentCounts(stoppedSeqIds);
      }
    } catch (error) {
      logger.error('Error loading stopped sequences:', error);
    }
  };

  const loadRecentSentSequences = async () => {
    try {
      // Get sequences with recent executions that were sent
      const { data, error } = await supabase
        .from('email_automation_sequences')
        .select(`
          id,
          lead_email,
          campaign_id,
          current_step,
          max_steps,
          status,
          total_sent,
          last_sent_at,
          original_inbox_email,
          email_automation_campaign_activation!inner(campaign_name)
        `)
        .not('last_sent_at', 'is', null)
        .order('last_sent_at', { ascending: false })
        .limit(10);
      
      if (error) {
        logger.error('Error loading recent sent sequences:', error);
        setRecentSentSequences([]);
        return;
      }
      
      // Transform data to include campaign_name at top level
      const transformedData = (data || []).map(seq => ({
        ...seq,
        campaign_name: seq.email_automation_campaign_activation?.campaign_name || 'Unknown Campaign',
        last_execution_time: seq.last_sent_at
      }));
      
      // Filter by assigned campaigns for non-admin users
      const filteredRecent = filterByCampaignName(transformedData);
      setRecentSentSequences(filteredRecent);
      
      // Load actual sent counts for recent sequences too
      if (data && data.length > 0) {
        const recentSeqIds = data.map(s => s.id);
        loadActualSentCounts(recentSeqIds);
      }
    } catch (error) {
      logger.error('Error in loadRecentSentSequences:', error);
      setRecentSentSequences([]);
    }
  };

  const loadFollowUpCalls = async () => {
    try {
      const rawCalls = await advancedEmailAutomationService.getFollowUpCallList(selectedCampaignForCalls || null);
      // Filter by assigned campaigns for non-admin users
      const calls = filterByCampaignName(rawCalls || []);
      setFollowUpCalls(calls);
      
      // Extract unique campaigns with calls for dropdown
      const uniqueCampaigns = [];
      const campaignMap = new Map();
      
      (calls || []).forEach(call => {
        if (!campaignMap.has(call.campaign_id)) {
          campaignMap.set(call.campaign_id, {
            campaign_id: call.campaign_id,
            campaign_name: call.campaign_name,
            call_count: 1
          });
        } else {
          campaignMap.get(call.campaign_id).call_count++;
        }
      });
      
      setCampaignsWithCalls(Array.from(campaignMap.values()));
    } catch (error) {
      logger.error('Error loading follow-up calls:', error);
      setFollowUpCalls([]);
      setCampaignsWithCalls([]);
    }
  };

  const deleteStoppedSequence = async (sequenceId, leadEmail) => {
    try {
      if (!window.confirm(`Are you sure you want to delete the stopped sequence for ${leadEmail}?`)) {
        return;
      }

      await advancedEmailAutomationService.deleteStoppedSequence(sequenceId);
      alert('Stopped sequence deleted successfully.');
      
      // Refresh data
      await loadStoppedSequences();
      await loadSequenceAnalytics();
    } catch (error) {
      logger.error('Error deleting stopped sequence:', error);
      alert('Error deleting stopped sequence. Please try again.');
    }
  };

  const toggleCampaignExpansion = (campaignId) => {
    const newExpanded = new Set(expandedCampaigns);
    if (newExpanded.has(campaignId)) {
      newExpanded.delete(campaignId);
    } else {
      newExpanded.add(campaignId);
    }
    setExpandedCampaigns(newExpanded);
  };

  const deleteCampaignSequences = async (campaignId, campaignName) => {
    try {
      const campaign = groupedStoppedSequences[campaignId];
      if (!campaign || !campaign.sequences.length) {
        return;
      }

      const sequenceCount = campaign.sequences.length;
      if (!window.confirm(`Are you sure you want to delete all ${sequenceCount} stopped sequences from campaign "${campaignName}"? This action cannot be undone.`)) {
        return;
      }

      // Delete all sequences for this campaign
      const deletePromises = campaign.sequences.map(sequence => 
        advancedEmailAutomationService.deleteStoppedSequence(sequence.id)
      );
      
      await Promise.all(deletePromises);
      alert(`Successfully deleted ${sequenceCount} stopped sequences from campaign "${campaignName}".`);
      
      // Refresh data
      await loadStoppedSequences();
      await loadSequenceAnalytics();
    } catch (error) {
      logger.error('Error deleting campaign sequences:', error);
      alert('Error deleting campaign sequences. Please try again.');
    }
  };
  



  // Delete a lead from follow-up calls
  const deleteFollowUpLead = async (leadId, leadEmail) => {
    try {
      setDeletingLead(leadId);
      
      if (!window.confirm(`Are you sure you want to delete ${leadEmail} from follow-up calls?`)) {
        return;
      }
      
      // Delete from follow-up call list
      const { error } = await supabase
        .from('email_automation_followup_calls')
        .delete()
        .eq('id', leadId);
      
      if (error) {
        logger.error('Error deleting follow-up lead:', error);
        alert('Error deleting lead. Please try again.');
        return;
      }
      
      alert('Lead deleted successfully.');
      await loadFollowUpCalls();
      
    } catch (error) {
      logger.error('Error deleting follow-up lead:', error);
      alert('Error deleting lead. Please try again.');
    } finally {
      setDeletingLead(null);
    }
  };
  
  // Run migration for existing leads
  const runExistingLeadsMigration = async () => {
    try {
      setMigrationRunning(true);
      setMigrationResults(null);
      
      logger.log('🚀 Starting migration of existing opened leads...');
      const results = await runMigration();
      
      setMigrationResults(results);
      setShowMigrationModal(true);
      
      // Refresh data after migration
      await loadFollowUpCalls();
      await loadSequences();
      
    } catch (error) {
      logger.error('Migration failed:', error);
      alert('Migration failed. Please check the console for details.');
    } finally {
      setMigrationRunning(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_automation_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error('Error loading templates:', error);
        setTemplates([]);
        return;
      }
      
      setTemplates(data || []);
    } catch (error) {
      logger.error('Error in loadTemplates:', error);
      setTemplates([]);
    }
  };

  const loadSequenceAnalytics = async () => {
    try {
      // For non-admin users, use the filtered RPC to get campaign-scoped stats
      if (!isAdminOrHigher() && assignedCampaigns && assignedCampaigns.length > 0) {
        const { data: overviewStats, error: overviewError } = await supabase.rpc('get_automation_overview_stats', {
          p_campaigns: assignedCampaigns
        });
        if (!overviewError && overviewStats && overviewStats.length > 0) {
          const stats = overviewStats[0];
          setSequenceAnalytics({
            total_sequences: parseInt(stats.total_sequences) || 0,
            active_sequences: parseInt(stats.active_sequences) || 0,
            stopped_sequences: parseInt(stats.stopped_sequences) || 0,
            completed_sequences: parseInt(stats.completed_sequences) || 0,
            replied_sequences: parseInt(stats.replied_sequences) || 0,
            total_emails_sent: parseInt(stats.total_emails_sent) || 0,
            campaigns: parseInt(stats.active_campaigns) || 0
          });
          return;
        }
      }
      // Admin/developer or RPC fallback: use full unfiltered analytics
      const analytics = await advancedEmailAutomationService.getSequenceAnalytics();
      setSequenceAnalytics(analytics || null);
    } catch (error) {
      setSequenceAnalytics(null);
    }
  };

  const loadEmailOpens = async (page = currentEmailOpensPage, stepFilter = opensStepFilter, searchQuery = opensSearchQuery) => {
    try {
      setOpensLoading(true);
      
      // Calculate offset for pagination
      const offset = (page - 1) * emailOpensPerPage;

      // Determine allowed campaign_ids for non-admin users
      // This ensures BOTH count and data queries are filtered at query level
      const allowedCampaignIds = (!isAdminOrHigher() && campaignActivations && campaignActivations.length > 0)
        ? campaignActivations.map(ca => ca.campaign_id)
        : null;

      // If non-admin with no campaigns, return empty immediately
      if (!isAdminOrHigher() && (!allowedCampaignIds || allowedCampaignIds.length === 0)) {
        setTotalEmailOpens(0);
        setTotalUniqueLeads(0);
        setTotalMultipleOpens(0);
        setEmailOpens([]);
        setCurrentEmailOpensPage(page);
        setOpensLoading(false);
        return;
      }

      // Build base count query with filters + campaign RLS
      let countQuery = supabase
        .from('email_automation_opens_dashboard')
        .select('*', { count: 'exact', head: true });

      // Apply campaign filter at query level for non-admin
      if (allowedCampaignIds) {
        countQuery = countQuery.in('campaign_id', allowedCampaignIds);
      }
      if (stepFilter !== 'all') {
        countQuery = countQuery.eq('sequence_step', parseInt(stepFilter));
      }
      if (searchQuery && searchQuery.trim()) {
        countQuery = countQuery.ilike('lead_email', `%${searchQuery.trim()}%`);
      }

      const { count, error: countError } = await countQuery;
      if (countError) {
        logger.error('Error getting email opens count:', countError);
      } else {
        setTotalEmailOpens(count || 0);
      }

      // Get all data for analytics stats (filtered by campaign at query level)
      let analyticsQuery = supabase
        .from('email_automation_opens_dashboard')
        .select('lead_email, open_count, campaign_id');
      if (allowedCampaignIds) {
        analyticsQuery = analyticsQuery.in('campaign_id', allowedCampaignIds);
      }
      
      const { data: allOpensData, error: allOpensError } = await analyticsQuery;
      
      if (!allOpensError && allOpensData) {
        const uniqueLeads = new Set(allOpensData.map(open => open.lead_email)).size;
        setTotalUniqueLeads(uniqueLeads);
        const multipleOpens = allOpensData.filter(open => open.open_count > 1).length;
        setTotalMultipleOpens(multipleOpens);
      }
      
      // Build paginated data query with filters + campaign RLS
      let dataQuery = supabase
        .from('email_automation_opens_dashboard')
        .select('*')
        .order('opened_at', { ascending: false });

      // Apply campaign filter at query level for non-admin
      if (allowedCampaignIds) {
        dataQuery = dataQuery.in('campaign_id', allowedCampaignIds);
      }
      if (stepFilter !== 'all') {
        dataQuery = dataQuery.eq('sequence_step', parseInt(stepFilter));
      }
      if (searchQuery && searchQuery.trim()) {
        dataQuery = dataQuery.ilike('lead_email', `%${searchQuery.trim()}%`);
      }

      dataQuery = dataQuery.range(offset, offset + emailOpensPerPage - 1);

      const { data, error } = await dataQuery;
      
      if (error) {
        logger.error('Error loading email opens:', error);
        setEmailOpens([]);
        return;
      }
      
      setEmailOpens(data || []);
      setTotalEmailOpens(count || 0);
      setCurrentEmailOpensPage(page);
    } catch (error) {
      logger.error('Error in loadEmailOpens:', error);
      setEmailOpens([]);
    } finally {
      setOpensLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const { error } = await supabase
        .from('email_automation_settings')
        .update(updates)
        .eq('id', settings?.id);
      
      if (error) {
        logger.error('Error updating settings:', error);
        return;
      }
      
      setSettings({ ...settings, ...updates });
    } catch (error) {
      logger.error('Error in updateSettings:', error);
    }
  };

  const sendTestEmail = async (template) => {
    try {
      setSendingTestEmail(true);
      logger.log('🧪 Sending test email for template:', template.name);
      
      // Enhanced test lead data with more realistic information
      const testLeadData = {
        first_name: 'Matti',
        last_name: 'Virtanen',
        email: 'aiman@otherwiselight.com',
        company: 'Rakennusliike Virtanen Oy',
        campaign_name: 'Valaistusratkaisut 2024',
        source: 'Template Test',
        lead_score: '92',
        current_date: new Date().toLocaleDateString('fi-FI')
      };
      
      // Process template content with enhanced sanitization and real lead data
      const emailContent = processTemplateContent(template, testLeadData);
      const emailSubject = processTemplateSubject(template, testLeadData);
      
      // Validate processed content
      if (!emailContent || !emailSubject) {
        throw new Error('Template processing failed - content or subject is empty');
      }
      
      // Log processed content for debugging
      logger.log('📧 Processed email subject:', emailSubject);
      logger.log('📧 Processed email content length:', emailContent.length);
      
      // Prepare webhook payload mimicking automation structure
      const webhookPayload = {
        action: 'send_automation_email',
        lead_email: testLeadData.email,
        campaign_id: 'test-campaign-' + Date.now(),
        campaign_name: 'Template Test Campaign',
        template_id: template.id,
        template_name: template.name,
        email_subject: emailSubject,
        email_content: emailContent,
        sender_email: 'jani@otherwiselight.org', // Default inbox email
        sender_name: 'Otherwise Light',
        lead_data: testLeadData,
        automation_data: {
          sequence_step: template.sequence_step || 1,
          delay_days: Math.round((template.delay_minutes || 10080) / 1440),
          is_test: true,
          test_timestamp: new Date().toISOString(),
          template_processing_version: '2.0'
        }
      };
      
      // Send to N8N webhook
      const response = await fetch(process.env.REACT_APP_N8N_EMAIL_AUTOMATION_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });
      
      if (response.ok) {
        logger.log('✅ Test email sent successfully');
        alert('✅ Test email sent successfully to aiman@otherwiselight.com!');
      } else {
        logger.error('❌ Failed to send test email:', response.statusText);
        alert('❌ Failed to send test email. Please check the console for details.');
      }
    } catch (error) {
      logger.error('Error sending test email:', error);
      alert('❌ Error sending test email: ' + error.message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const activateCampaign = async () => {
    try {
      // Use DB-level activation that handles everything:
      // - Creates/updates campaign activation record
      // - Moves pending followup_calls to sequences with proper campaign_activation_id
      // - Sets correct next_scheduled_at using time window
      // - pg_cron orchestrator will automatically create executions for due sequences
      const { data: result, error } = await supabase
        .rpc('activate_campaign_with_sequences', {
          p_campaign_id: newActivation.campaign_id,
          p_campaign_name: newActivation.campaign_name,
          p_template_id: newActivation.template_id || null,
          p_sequence_config: newActivation.sequence_config,
          p_sending_time_window: newActivation.sending_time_window
        });

      if (error) {
        logger.error('Error activating campaign via DB:', error);
        alert(`Error activating campaign: ${error.message}`);
        return;
      }

      if (result && result.success) {
        logger.log(`✅ Campaign activated via DB:`, result);

        const created    = result.sequences_created   ?? 0;
        const restarted  = result.sequences_restarted ?? 0;
        const skipCompl  = result.skipped_completed   ?? 0;
        const skipActv   = result.skipped_active      ?? 0;
        const skipRepl   = result.skipped_replied     ?? 0;
        const skipReply  = result.skipped_reply       ?? 0;
        const total      = result.total_processed
          ?? (created + restarted + skipCompl + skipActv + skipRepl + skipReply);

        const lines = ['Campaign activated successfully!', ''];
        lines.push(`Total follow-up leads processed: ${total}`);
        if (created   > 0) lines.push(`✅ ${created} new sequence${created   === 1 ? '' : 's'} created`);
        if (restarted > 0) lines.push(`🔄 ${restarted} sequence${restarted === 1 ? '' : 's'} restarted (paused/stopped)`);
        if (skipCompl > 0) lines.push(`⏭️ ${skipCompl} skipped — already completed the sequence`);
        if (skipActv  > 0) lines.push(`⏭️ ${skipActv} skipped — sequence already running`);
        if (skipRepl  > 0) lines.push(`🛑 ${skipRepl} skipped — sequence marked as replied`);
        if (skipReply > 0) lines.push(`🛑 ${skipReply} skipped — lead already replied`);
        if (created === 0 && restarted === 0) {
          lines.push('');
          lines.push('No new emails will be sent — all leads were already handled.');
        } else {
          lines.push('');
          lines.push('The orchestrator will automatically process new/restarted sequences.');
        }

        alert(lines.join('\n'));
      } else {
        logger.warn('Activation returned unexpected result:', result);
        alert('Campaign activation completed.');
      }
        
      setShowCampaignActivationModal(false);
      setNewActivation({
        campaign_id: '',
        campaign_name: '',
        template_id: '',
        sequence_config: {
          steps: [
            { step: 1, delay_days: 7, template_id: null },
            { step: 2, delay_days: 15, template_id: null },
            { step: 3, delay_days: 15, template_id: null },
            { step: 4, delay_days: 15, template_id: null },
            { step: 5, delay_days: 15, template_id: null }
          ],
          stop_on_reply: true,
          max_sequences: 5
        },
        sending_time_window: {
          start_time: '01:00',
          end_time: '09:00',
          timezone: 'Europe/Helsinki',
          randomize_within_window: true,
          min_delay_minutes: 1,
          max_delay_minutes: 10
        }
      });
      await loadCampaignActivations();
      await loadFollowUpCalls();
      await loadSequenceAnalytics();
    } catch (error) {
      logger.error('Error activating campaign:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const deactivateCampaign = async (campaignId) => {
    try {
      logger.log(`🔄 Starting deactivation process for campaign ${campaignId}`);
      
      // Use the enhanced database function for proper deactivation
      const { data: result, error } = await supabase
        .rpc('deactivate_campaign_with_sequences', {
          p_campaign_id: campaignId,
          p_deactivated_by: 'user'
        });

      if (error) {
        logger.error('Error deactivating campaign:', error);
        alert(`Error deactivating campaign: ${error.message}`);
        return;
      }
      
      if (result) {
        logger.log(`✅ Campaign ${campaignId} deactivated successfully:`, result);
        logger.log(`- Stopped ${result.stopped_sequences || 0} sequences`);
        logger.log(`- Cancelled ${result.cancelled_executions || 0} pending executions`);
        logger.log(`- Restored ${result.restored_calls || 0} follow-up calls`);
         
        alert(`Campaign deactivated successfully!\n- Stopped ${result.stopped_sequences || 0} sequences\n- Cancelled ${result.cancelled_executions || 0} pending executions\n- Restored ${result.restored_calls || 0} follow-up calls`);
      } else {
        logger.warn('No result returned from deactivation function');
        alert('Campaign deactivated, but no detailed results available.');
      }

      // Refresh all related data
      await loadCampaignActivations();
      await loadFollowUpCalls();
      await loadStoppedSequences();
      await loadSequenceAnalytics();
    } catch (error) {
      logger.error('Error in deactivate campaign:', error);
      alert(`Error deactivating campaign: ${error.message}`);
    }
  };

  const updateCampaignConfiguration = async (campaignId, sequenceConfig, sendingTimeWindow) => {
    try {
      logger.log(`🔧 Updating configuration for campaign ${campaignId}`);
      logger.log('New sequence config:', sequenceConfig);
      logger.log('New time window:', sendingTimeWindow);
      
      // Use the new RPC endpoint to update campaign and auto-reconfigure sequences
      const { data: result, error } = await supabase
        .rpc('update_campaign_activation', {
          p_campaign_id: campaignId,
          p_sequence_config: sequenceConfig,
          p_sending_time_window: sendingTimeWindow
        });

      if (error) {
        logger.error('Error updating campaign configuration:', error);
        alert(`Error updating campaign: ${error.message}`);
        return false;
      }
      
      if (result && result.success) {
        logger.log(`✅ Campaign ${campaignId} updated successfully:`, result);
        logger.log(`- Updated ${result.sequences_updated || 0} sequences`);
        logger.log(`- Cancelled ${result.executions_cancelled || 0} obsolete executions`);
         
        alert(`Campaign updated successfully!\n✅ Rescheduled ${result.sequences_updated || 0} active sequences\n✅ Cancelled ${result.executions_cancelled || 0} obsolete executions\n\nAll pending emails will now use the new configuration.`);
        
        // Refresh all related data to show updated schedules
        await loadCampaignActivations();
        await loadSequences(currentSequencePage, sequenceStepFilter);
        await loadSequenceAnalytics();
        
        return true;
      } else {
        logger.warn('Update completed but no result details returned');
        alert('Campaign configuration updated.');
        await loadCampaignActivations();
        return true;
      }
    } catch (error) {
      logger.error('Error in updateCampaignConfiguration:', error);
      alert(`Error updating campaign configuration: ${error.message}`);
      return false;
    }
  };

  const updateSequenceConfig = (stepIndex, field, value) => {
    const updatedSteps = [...newActivation.sequence_config.steps];
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], [field]: value };
    setNewActivation({
      ...newActivation,
      sequence_config: {
        ...newActivation.sequence_config,
        steps: updatedSteps
      }
    });
  };

  // Template Management Functions
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setNewTemplate({
      name: '',
      description: '',
      trigger_event: 'open',
      subject_template: '',
      content_template: '',
      delay_minutes: 10080, // 7 days in minutes
      is_active: true,
      is_sequence_template: false,
      sequence_step: 1,
      sequence_config: {},
      follow_up_subject: '',
      follow_up_content: ''
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({ ...template });
    setShowTemplateModal(true);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  // Template validation and sanitization
  const validateTemplate = (template) => {
    const errors = [];
    
    if (!template.name || template.name.trim().length === 0) {
      errors.push('Template name is required');
    }
    
    if (template.name && template.name.length > 100) {
      errors.push('Template name must be less than 100 characters');
    }
    
    if (!template.subject_template || template.subject_template.trim().length === 0) {
      errors.push('Subject template is required');
    }
    
    if (!template.content_template || template.content_template.trim().length === 0) {
      errors.push('Content template is required');
    }
    
    if (template.delay_minutes && (template.delay_minutes < 60 || template.delay_minutes > 525600)) {
      errors.push('Delay must be between 1 hour and 365 days');
    }
    
    if (template.sequence_step && (template.sequence_step < 1 || template.sequence_step > 10)) {
      errors.push('Sequence step must be between 1 and 10');
    }
    
    return errors;
  };

  const saveTemplate = async () => {
    try {
      // Validate template data
      const validationErrors = validateTemplate(newTemplate);
      if (validationErrors.length > 0) {
        alert('Please fix the following errors:\n' + validationErrors.join('\n'));
        return;
      }
      
      // Sanitize template data before saving
      const templateData = {
        ...newTemplate,
        name: sanitizeInput(newTemplate.name),
        description: sanitizeInput(newTemplate.description),
        subject_template: sanitizeInput(newTemplate.subject_template),
        content_template: sanitizeInput(newTemplate.content_template),
        follow_up_subject: sanitizeInput(newTemplate.follow_up_subject || ''),
        follow_up_content: sanitizeInput(newTemplate.follow_up_content || '')
      };
      
      if (editingTemplate) {
        // Update existing template
        const { error } = await supabase
          .from('email_automation_templates')
          .update(templateData)
          .eq('id', editingTemplate.id);
        
        if (error) throw error;
      } else {
        // Create new template
        const { error } = await supabase
          .from('email_automation_templates')
          .insert([templateData]);
        
        if (error) throw error;
      }
      
      setShowTemplateModal(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error) {
      logger.error('Error saving template:', error);
      alert('Error saving template: ' + error.message);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const { error } = await supabase
        .from('email_automation_templates')
        .delete()
        .eq('id', templateId);
      
      if (error) throw error;
      await loadTemplates();
    } catch (error) {
      logger.error('Error deleting template:', error);
    }
  };

  // XSS Protection and Input Sanitization
  const sanitizeInput = (input) => {
    if (!input || typeof input !== 'string') return '';
    
    return input
      .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '') // Remove iframe tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .replace(/data:/gi, '') // Remove data: protocol
      .replace(/vbscript:/gi, '') // Remove vbscript: protocol
      .trim();
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Enhanced template processing with real lead data support
  const processTemplateContent = (template, leadData = null) => {
    if (!template) return '';
    
    let content = sanitizeInput(template.content_template || '');
    
    // Use real lead data if provided, otherwise use sample data
    const data = leadData || {
      first_name: 'Matti',
      last_name: 'Virtanen',
      email: 'matti.virtanen@example.com',
      company: 'Rakennusliike Virtanen Oy',
      campaign_name: 'Valaistusratkaisut 2024',
      source: 'Email Marketing',
      lead_score: '85',
      current_date: new Date().toLocaleDateString('fi-FI')
    };
    
    // Sanitize all data values
    const sanitizedData = {};
    Object.keys(data).forEach(key => {
      sanitizedData[key] = escapeHtml(String(data[key] || ''));
    });
    
    // Replace template variables with sanitized data
    const variables = {
      first_name: sanitizedData.first_name || 'there',
      last_name: sanitizedData.last_name || '',
      email: sanitizedData.email || '',
      company: sanitizedData.company || 'your company',
      campaign_name: sanitizedData.campaign_name || 'our campaign',
      source: sanitizedData.source || 'our outreach',
      lead_score: sanitizedData.lead_score || '0',
      current_date: sanitizedData.current_date || new Date().toLocaleDateString('fi-FI')
    };
    
    // Replace variables with proper escaping
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      content = content.replace(regex, value);
    });
    
    return content;
  };

  // For backward compatibility
  const processPreviewContent = (template) => {
    return processTemplateContent(template, null);
  };

  // Function to test template with real lead data from database
  const testTemplateWithRealData = async (template, leadEmail = null) => {
    try {
      let leadData = null;
      
      if (leadEmail) {
        // Fetch real lead data from database
        const { data, error } = await supabase
          .from('lead_events')
          .select('*')
          .eq('lead_email', leadEmail)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (!error && data) {
          // Extract lead data from raw_data or use available fields
          const rawData = data.raw_data || {};
          leadData = {
            first_name: rawData.first_name || data.first_name || 'Valued Customer',
            last_name: rawData.last_name || data.last_name || '',
            email: data.lead_email,
            company: rawData.company || data.company || 'Your Company',
            campaign_name: data.campaign_name || 'Our Campaign',
            source: rawData.source || data.source || 'Email Marketing',
            lead_score: rawData.lead_score || '0',
            current_date: new Date().toLocaleDateString('fi-FI')
          };
        }
      }
      
      // Process template with real or sample data
      const emailContent = processTemplateContent(template, leadData);
      const emailSubject = processTemplateSubject(template, leadData);
      
      return {
        success: true,
        emailContent,
        emailSubject,
        leadData: leadData || 'sample data used'
      };
    } catch (error) {
      logger.error('Error testing template with real data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Enhanced subject processing with sanitization
  const processTemplateSubject = (template, leadData = null) => {
    if (!template) return '';
    
    let subject = sanitizeInput(template.subject_template || '');
    
    // Use real lead data if provided, otherwise use sample data
    const data = leadData || {
      first_name: 'Matti',
      last_name: 'Virtanen',
      email: 'matti.virtanen@example.com',
      company: 'Rakennusliike Virtanen Oy',
      campaign_name: 'Valaistusratkaisut 2024',
      source: 'Email Marketing',
      lead_score: '85',
      current_date: new Date().toLocaleDateString('fi-FI')
    };
    
    // Sanitize all data values
    const sanitizedData = {};
    Object.keys(data).forEach(key => {
      sanitizedData[key] = escapeHtml(String(data[key] || ''));
    });
    
    // Replace template variables with sanitized data
    const variables = {
      first_name: sanitizedData.first_name || 'there',
      last_name: sanitizedData.last_name || '',
      email: sanitizedData.email || '',
      company: sanitizedData.company || 'your company',
      campaign_name: sanitizedData.campaign_name || 'our campaign',
      source: sanitizedData.source || 'our outreach',
      lead_score: sanitizedData.lead_score || '0',
      current_date: sanitizedData.current_date || new Date().toLocaleDateString('fi-FI')
    };
    
    // Replace variables with proper escaping
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      subject = subject.replace(regex, value);
    });
    
    return subject;
  };

  // For backward compatibility
  const getPreviewSubject = (template) => {
    return processTemplateSubject(template, null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'replied': return 'text-purple-600 bg-purple-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      case 'stopped': return 'text-red-600 bg-red-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getValidStep = (sequence) => {
    const currentStep = sequence.current_step || 1;
    const maxSteps = sequence.max_steps || 5;
    const isCompleted = sequence.status === 'completed';
    const computedStep = isCompleted ? maxSteps : Math.min(currentStep, maxSteps);
    const validSent = isCompleted ? maxSteps : Math.max(0, currentStep - 1);
    return { validSent, computedStep, maxSteps, isCompleted };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading advanced automation system...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Zap className="w-8 h-8 mr-3 text-blue-600" />
              Advanced Email Automation
            </h1>
            <p className="text-gray-600 mt-2">
              Complete automation system with sequences, campaign activation, and follow-up management
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              settings?.is_enabled 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {settings?.is_enabled ? 'System Active' : 'System Inactive'}
            </div>
            {isAdminOrHigher() && (
              <button
                onClick={() => setShowCampaignActivationModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Activate Campaign
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                  <p className="text-xl font-bold text-blue-600">{activeCampaigns.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Active Sequences</p>
                  <p className="text-xl font-bold text-blue-600">{sequenceAnalytics?.active_sequences || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Follow-up Calls</p>
                  <p className="text-xl font-bold text-blue-600">{followUpCalls.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Emails Sent</p>
                  <p className="text-xl font-bold text-blue-600">{sequenceAnalytics?.total_emails_sent || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
            <h3 className="text-base font-semibold text-gray-900 mb-3">System Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-md">
                <p className="text-sm font-medium text-gray-600">Sequence Management</p>
                <p className={`text-base font-semibold ${
                  settings?.sequence_management_enabled ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {settings?.sequence_management_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className="bg-white p-3 rounded-md">
                <p className="text-sm font-medium text-gray-600">Duplicate Protection</p>
                <p className={`text-base font-semibold ${
                  settings?.duplicate_protection_enabled ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {settings?.duplicate_protection_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className="bg-white p-3 rounded-md">
                <p className="text-sm font-medium text-gray-600">Time Windows</p>
                <p className={`text-base font-semibold ${
                  settings?.time_window_enabled ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {settings?.time_window_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </div>

          {/* Recent Sequences */}
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Recent Sequences</h3>
            <div className="space-y-3">
              {recentSentSequences.slice(0, 10).map(sequence => (
                <div key={sequence.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(sequence.status).replace('text-', 'bg-').replace(' bg-', ' ')}`}></div>
                    <div>
                      <p className="font-medium text-gray-900">{sequence.lead_email}</p>
                      <p className="text-xs text-gray-600">{sequence.campaign_name}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Inbox className="w-3 h-3" />
                        {sequence.original_inbox_email}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {(() => {
                      const { validSent, computedStep, maxSteps, isCompleted } = getValidStep(sequence);
                      return (
                        <>
                          <p className="text-sm font-medium text-gray-900">{isCompleted ? `Done ${maxSteps}/${maxSteps}` : `Step ${computedStep}/${maxSteps}`}</p>
                          <p className="text-xs text-gray-600">{validSent} sent</p>
                        </>
                      );
                    })()}
                    <p className="text-xs text-gray-500 mt-0.5">
                      {new Date(sequence.last_execution_time).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))}
              {recentSentSequences.length === 0 && (
                <div className="text-center text-gray-500 py-4">
                  <p className="text-sm">No recent sent events</p>
                  <p className="text-xs mt-1">Sequences will appear here when emails are sent</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Campaign Activation Tab */}
      {activeTab === 'campaigns' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Campaign Activation Management</h3>
            <button
              onClick={() => setShowCampaignActivationModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Activate New Campaign
            </button>
          </div>

          {/* Active Campaigns */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 flex items-center">
                <Target className="w-5 h-5 mr-2 text-green-600" />
                Active Campaigns ({activeCampaigns.length})
              </h4>
            </div>
            <div className="divide-y divide-gray-200">
              {activeCampaigns.map(activation => (
                <div key={activation.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h5 className="font-medium text-gray-900">{activation.campaign_name}</h5>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        <p>Campaign ID: {activation.campaign_id}</p>
                        <p>Template: {activation.email_automation_templates?.name || 'Default'}</p>
                        <p>Max Steps: {activation.sequence_config?.max_sequences || 5}</p>
                        <p>Time Window: {activation.sending_time_window?.start_time} - {activation.sending_time_window?.end_time}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedCampaign(activation);
                          setCampaignStepData(null);
                          loadCampaignStepData(activation.campaign_id);
                          setShowSequenceConfigModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Configure
                      </button>
                      <button
                        onClick={() => deactivateCampaign(activation.campaign_id)}
                        className="text-red-600 hover:text-red-800 font-medium text-sm flex items-center"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Deactivate
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Overdue Leads Section */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-md font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                Overdue Leads ({overdueSequences.length})
              </h4>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => loadOverdueSequences()}
                  disabled={overdueLoading}
                  className="text-gray-500 hover:text-gray-700 p-1"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${overdueLoading ? 'animate-spin' : ''}`} />
                </button>
                {overdueSequences.length > 0 && (
                  <button
                    onClick={() => handleRescheduleOverdue()}
                    disabled={reschedulingOverdue}
                    className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center"
                  >
                    {reschedulingOverdue ? (
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Repeat className="w-3 h-3 mr-1" />
                    )}
                    Reschedule All
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {overdueSequences.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium">No overdue leads</p>
                  <p className="text-xs mt-1">All sequences are on schedule</p>
                </div>
              ) : (
                overdueSequences.map((seq) => (
                  <div key={seq.sequence_id} className="p-4 hover:bg-red-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{seq.lead_email}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{seq.campaign_name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                            Step {seq.current_step}/{seq.max_steps}
                          </span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                            {seq.total_sent} sent
                          </span>
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">
                            {seq.overdue_hours}h overdue
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRescheduleOverdue(seq.campaign_id)}
                        disabled={reschedulingOverdue}
                        className="text-orange-600 hover:text-orange-800 text-xs font-medium flex items-center ml-2"
                        title="Reschedule this campaign's overdue sequences"
                      >
                        <Repeat className="w-3 h-3 mr-1" />
                        Reschedule
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Available Campaigns */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-semibold text-gray-900">Available Campaigns</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {campaigns
                .filter(campaign => !activeCampaigns.find(ac => ac.campaign_id === campaign.campaign_id))
                .map(campaign => (
                <div key={campaign.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900">{campaign.campaign_name}</h5>
                      <div className="mt-1 text-sm text-gray-600">
                        <p>Campaign ID: {campaign.campaign_id}</p>
                        <p>Total Leads: {campaign.total_leads}</p>
                        <p>Opens: {campaign.total_opens}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setNewActivation({
                          ...newActivation,
                          campaign_id: campaign.campaign_id,
                          campaign_name: campaign.campaign_name
                        });
                        setShowCampaignActivationModal(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Activate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Sequences Tab */}
      {activeTab === 'sequences' && (
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Follow-up Sequences</h3>
              <div className="flex items-center space-x-3">
                <select 
                  value={sequenceCampaignFilter}
                  onChange={(e) => {
                    setSequenceCampaignFilter(e.target.value);
                    loadSequences(1, sequenceStepFilter, sequenceSearchQuery, e.target.value);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Campaigns</option>
                  {campaignActivations.map(campaign => (
                    <option key={campaign.id} value={campaign.campaign_id}>
                      {campaign.campaign_name}{!campaign.is_active ? ' (inactive)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => loadSequences(1, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by lead email..."
                  value={sequenceSearchQuery}
                  onChange={(e) => setSequenceSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      loadSequences(1, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <button
                onClick={() => loadSequences(1, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Search
              </button>
              {sequenceSearchQuery && (
                <button
                  onClick={() => {
                    setSequenceSearchQuery('');
                    loadSequences(1, sequenceStepFilter, '', sequenceCampaignFilter);
                  }}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg text-sm"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Sequence Analytics */}
          {sequenceAnalytics && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-white" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-gray-600">Total Sequences</p>
                    <p className="text-lg font-bold text-blue-600">{sequenceAnalytics?.total_sequences || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Play className="w-4 h-4 text-white" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-gray-600">Active</p>
                    <p className="text-lg font-bold text-blue-600">{sequenceAnalytics?.active_sequences || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Pause className="w-4 h-4 text-white" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-gray-600">Stopped</p>
                    <p className="text-lg font-bold text-blue-600">{sequenceAnalytics.stopped_sequences || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-gray-600">Completed</p>
                    <p className="text-lg font-bold text-blue-600">{sequenceAnalytics?.completed_sequences || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-gray-600">Replied</p>
                    <p className="text-lg font-bold text-blue-600">{sequenceAnalytics?.replied_sequences || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Active Sequences Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Play className="w-5 h-5 mr-2 text-green-600" />
                  Active Sequences ({totalSequences})
                </h4>
                <button
                  onClick={() => loadSequences(currentSequencePage, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              {/* Step Filter Buttons */}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => loadSequences(1, 'all', sequenceSearchQuery, sequenceCampaignFilter)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    sequenceStepFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  All Steps
                </button>
                {[1, 2, 3, 4, 5].map(step => (
                  <button
                    key={step}
                    onClick={() => loadSequences(1, step.toString(), sequenceSearchQuery, sequenceCampaignFilter)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      sequenceStepFilter === step.toString()
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Step {step}
                  </button>
                ))}
                <button
                  onClick={() => loadSequences(1, 'completed', sequenceSearchQuery, sequenceCampaignFilter)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    sequenceStepFilter === 'completed'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Scheduled
                  </th>
                  <th className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Sent
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sequences.map(sequence => (
                  <tr key={sequence.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{sequence.lead_email}</div>
                      <div className="text-sm text-gray-500">Inbox: {sequence.original_inbox_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sequence.email_automation_campaign_activation?.campaign_name || sequence.campaign_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(sequence.status)}`}>
                        {sequence.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const { validSent, computedStep, maxSteps, isCompleted } = getValidStep(sequence);
                        return (
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {isCompleted ? `Done ${maxSteps}/${maxSteps}` : `Step ${computedStep}/${maxSteps}`}
                            </div>
                            <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-blue-600'}`}
                                style={{ width: `${(Math.min(validSent, maxSteps) / maxSteps) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sequence.next_scheduled_at ? formatDate(sequence.next_scheduled_at) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-lg font-bold text-blue-600">
                        {getValidStep(sequence).validSent}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls for Active Sequences */}
            {totalSequences > sequencesPerPage && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((currentSequencePage - 1) * sequencesPerPage) + 1} to{' '}
                    {Math.min(currentSequencePage * sequencesPerPage, totalSequences)} of{' '}
                    {totalSequences} sequences
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => loadSequences(currentSequencePage - 1, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter)}
                      disabled={currentSequencePage === 1}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        currentSequencePage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.ceil(totalSequences / sequencesPerPage) }, (_, i) => i + 1)
                      .filter(page => {
                        const totalPages = Math.ceil(totalSequences / sequencesPerPage);
                        if (totalPages <= 7) return true;
                        if (page === 1 || page === totalPages) return true;
                        if (page >= currentSequencePage - 2 && page <= currentSequencePage + 2) return true;
                        return false;
                      })
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <button
                            onClick={() => loadSequences(page, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter)}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              currentSequencePage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                    
                    <button
                      onClick={() => loadSequences(currentSequencePage + 1, sequenceStepFilter, sequenceSearchQuery, sequenceCampaignFilter)}
                      disabled={currentSequencePage >= Math.ceil(totalSequences / sequencesPerPage)}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        currentSequencePage >= Math.ceil(totalSequences / sequencesPerPage)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Stopped Sequences Section */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Pause className="w-5 h-5 mr-2 text-red-600" />
                  Stopped Sequences ({stoppedSequences.length})
                </h4>
                <button
                  onClick={loadStoppedSequences}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {stoppedSequences.length === 0 ? (
              <div className="text-center py-8">
                <Pause className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No stopped sequences found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {Object.entries(groupedStoppedSequences).map(([campaignId, campaign]) => (
                  <div key={campaignId} className="bg-white">
                    {/* Campaign Header */}
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleCampaignExpansion(campaignId)}
                            className="flex items-center text-gray-700 hover:text-gray-900 font-medium"
                          >
                            {expandedCampaigns.has(campaignId) ? (
                              <ChevronDown className="w-5 h-5 mr-2" />
                            ) : (
                              <ChevronRight className="w-5 h-5 mr-2" />
                            )}
                            <span className="text-lg font-semibold">{campaign.name}</span>
                          </button>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {campaign.sequences.length} sequences
                          </span>
                        </div>
                        {canDelete() && (
                          <button
                            onClick={() => deleteCampaignSequences(campaignId, campaign.name)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete All
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Campaign Sequences */}
                    {expandedCampaigns.has(campaignId) && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Lead Email
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Progress
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Stopped At
                              </th>
                              <th className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                Sent
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {campaign.sequences.map(sequence => (
                              <tr key={sequence.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{sequence.lead_email}</div>
                                  <div className="text-sm text-gray-500">Inbox: {sequence.original_inbox_email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {(() => {
                                    const { validSent, computedStep, maxSteps, isCompleted } = getValidStep(sequence);
                                    return (
                                      <div className="flex items-center">
                                        <div className="text-sm font-medium text-gray-900">
                                          {isCompleted ? `Done ${maxSteps}/${maxSteps}` : `Step ${computedStep}/${maxSteps}`}
                                        </div>
                                        <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                                          <div 
                                            className={`h-2 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-red-600'}`}
                                            style={{ width: `${(Math.min(validSent, maxSteps) / maxSteps) * 100}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {sequence.updated_at ? formatDate(sequence.updated_at) : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {getValidStep(sequence).validSent}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  {canDelete() && (
                                    <button
                                      onClick={() => deleteStoppedSequence(sequence.id, sequence.lead_email)}
                                      className="text-red-600 hover:text-red-900 flex items-center"
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Delete
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Opens Tab */}
      {activeTab === 'opens' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Email Opens Analytics</h3>
              <p className="text-sm text-gray-600 mt-1">Track email engagement from automation sequences</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={loadEmailOpens}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                disabled={opensLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${opensLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Email Opens Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg shadow border border-blue-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-700">Total Opens</p>
                  <p className="text-xl font-bold text-blue-900">{totalEmailOpens}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow border border-blue-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-700">Unique Leads</p>
                  <p className="text-xl font-bold text-blue-900">{totalUniqueLeads}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow border border-blue-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Repeat className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-700">Multiple Opens</p>
                  <p className="text-xl font-bold text-blue-900">{totalMultipleOpens}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow border border-blue-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-700">Score Boost</p>
                  <p className="text-xl font-bold text-blue-900">+5</p>
                </div>
              </div>
            </div>
          </div>

          {/* Email Opens Table with Step Tabs + Search */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col gap-3">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  Recent Email Opens ({totalEmailOpens})
                </h4>

                {/* Step Filter Tabs */}
                <div className="flex flex-wrap gap-2">
                  {['all', '1', '2', '3', '4', '5'].map(step => (
                    <button
                      key={step}
                      onClick={() => setOpensStepFilter(step)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                        opensStepFilter === step
                          ? 'bg-blue-600 text-white shadow'
                          : 'bg-white text-gray-600 border border-gray-300 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      {step === 'all' ? 'All Steps' : `Step ${step}`}
                    </button>
                  ))}
                </div>

                {/* Lead Email Search */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by lead email..."
                      value={opensSearchQuery}
                      onChange={e => setOpensSearchQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          setOpensSearchActive(true);
                          setCurrentEmailOpensPage(1);
                          loadEmailOpens(1, opensStepFilter, opensSearchQuery);
                        }
                      }}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setOpensSearchActive(true);
                      setCurrentEmailOpensPage(1);
                      loadEmailOpens(1, opensStepFilter, opensSearchQuery);
                    }}
                    className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Search
                  </button>
                  {(opensSearchActive || opensSearchQuery) && (
                    <button
                      onClick={() => {
                        setOpensSearchQuery('');
                        setOpensSearchActive(false);
                        setCurrentEmailOpensPage(1);
                        loadEmailOpens(1, opensStepFilter, '');
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
                    >
                      <X className="w-3.5 h-3.5" /> Clear
                    </button>
                  )}
                </div>

                {opensSearchActive && opensSearchQuery && (
                  <p className="text-xs text-blue-600">
                    Showing all steps opened by: <strong>{opensSearchQuery}</strong>
                  </p>
                )}
              </div>
            </div>

          {opensLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Loading email opens...</p>
                <p className="text-sm text-gray-500 mt-1">Please wait while we fetch the latest data</p>
              </div>
            </div>
          ) : emailOpens.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Eye className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No email opens yet</h3>
              <p className="text-gray-500 mb-4">Opens will appear here when leads engage with automation emails</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> Make sure your email automation sequences are active and tracking pixels are enabled
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Pagination Info */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing {Math.min((currentEmailOpensPage - 1) * emailOpensPerPage + 1, totalEmailOpens)} to{' '}
                    {Math.min(currentEmailOpensPage * emailOpensPerPage, totalEmailOpens)} of {totalEmailOpens} opens
                  </p>
                  <button
                    onClick={loadEmailOpens}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <table className="min-w-full divide-y divide-gray-200">
               <thead className="bg-gray-50">
                 <tr>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Lead
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Campaign
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Sequence
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Opens
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Last Opened
                   </th>
                 </tr>
               </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {emailOpens.map(open => {
                  const handleViewLead = async () => {
                    try {
                      const { data: leadData, error } = await supabase
                        .from('leads')
                        .select('*')
                        .eq('email', open.lead_email)
                        .single();
                      if (error) {
                        const campaign = campaigns.find(c => c.campaign_id === open.campaign_id);
                        window.dispatchEvent(new CustomEvent('viewLead', { detail: { lead: { email: open.lead_email, first_name: open.first_name, last_name: open.last_name, company: open.company, campaign_name: campaign?.campaign_name || open.campaign_id, score: 0 } } }));
                        return;
                      }
                      const campaign = campaigns.find(c => c.campaign_id === open.campaign_id);
                      window.dispatchEvent(new CustomEvent('viewLead', { detail: { lead: { ...leadData, campaign_name: campaign?.campaign_name || leadData.campaign_name || open.campaign_id } } }));
                    } catch (err) {
                      const campaign = campaigns.find(c => c.campaign_id === open.campaign_id);
                      window.dispatchEvent(new CustomEvent('viewLead', { detail: { lead: { email: open.lead_email, first_name: open.first_name, last_name: open.last_name, company: open.company, campaign_name: campaign?.campaign_name || open.campaign_id, score: 0 } } }));
                    }
                  };

                    return (
                      <tr 
                        key={open.id} 
                        className="hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                        onClick={handleViewLead}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-600">
                                  {(open.first_name || open.lead_email).charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                {open.first_name || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-900 mt-1">{open.lead_email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {(() => {
                              const campaign = campaigns.find(c => c.campaign_id === open.campaign_id);
                              return campaign?.campaign_name || open.campaign_id;
                            })()}
                          </div>
                          <div className="text-xs text-gray-500">via {open.original_inbox_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              Step {open.sequence_step || 1}
                            </span>
                            {open.sequence_status && (
                              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                                open.sequence_status === 'active' ? 'bg-green-100 text-green-800' :
                                open.sequence_status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                open.sequence_status === 'replied' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {open.sequence_status}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 text-blue-500 mr-1" />
                            <span className="text-sm font-medium text-blue-600">
                              {open.open_count}
                            </span>
                            {open.open_count > 1 && (
                              <span className="ml-1 text-xs text-gray-500">times</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(open.last_opened_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Pagination Controls */}
              {(() => {
                const totalPages = Math.ceil(totalEmailOpens / emailOpensPerPage);
                
                if (totalPages <= 1) return null;
                
                return (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentEmailOpensPage(Math.max(1, currentEmailOpensPage - 1))}
                          disabled={currentEmailOpensPage === 1}
                          className={`px-3 py-1 rounded text-sm font-medium ${
                            currentEmailOpensPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          Previous
                        </button>
                        
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentEmailOpensPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentEmailOpensPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentEmailOpensPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentEmailOpensPage(pageNum)}
                                className={`px-3 py-1 rounded text-sm font-medium ${
                                  currentEmailOpensPage === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => setCurrentEmailOpensPage(Math.min(totalPages, currentEmailOpensPage + 1))}
                          disabled={currentEmailOpensPage === totalPages}
                          className={`px-3 py-1 rounded text-sm font-medium ${
                            currentEmailOpensPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          Next
                        </button>
                      </div>
                      
                      <div className="text-sm text-gray-700">
                        Page {currentEmailOpensPage} of {totalPages}
                      </div>
                    </div>
                  </div>
                );
              })()}
              </>
            )}
          </div>
        </div>
      )}

      {/* Follow-up Calls Tab */}
      {activeTab === 'followup-calls' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Follow-up Call List</h3>
            <div className="flex items-center space-x-4">
              <button
                onClick={loadFollowUpCalls}
                className="text-gray-600 hover:text-gray-800"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Campaign Selection Dropdown */}
          <div className="bg-white rounded-lg shadow border p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-gray-900">Select Campaign</h4>
              <span className="text-sm text-gray-600">
                {campaignsWithCalls.length} campaigns with pending calls
              </span>
            </div>
            
            {/* Campaign Pagination */}
            {campaignsWithCalls.length > campaignsPerPage && (
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCurrentCampaignPage(Math.max(1, currentCampaignPage - 1))}
                  disabled={currentCampaignPage === 1}
                  className={`px-3 py-2 rounded-lg font-medium ${
                    currentCampaignPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Previous
                </button>
                
                <div className="text-sm text-gray-700">
                  Page {currentCampaignPage} of {Math.ceil(campaignsWithCalls.length / campaignsPerPage)}
                </div>
                
                <button
                  onClick={() => setCurrentCampaignPage(Math.min(Math.ceil(campaignsWithCalls.length / campaignsPerPage), currentCampaignPage + 1))}
                  disabled={currentCampaignPage >= Math.ceil(campaignsWithCalls.length / campaignsPerPage)}
                  className={`px-3 py-2 rounded-lg font-medium ${
                    currentCampaignPage >= Math.ceil(campaignsWithCalls.length / campaignsPerPage)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
            
            {/* Campaign Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaignsWithCalls
                .slice((currentCampaignPage - 1) * campaignsPerPage, currentCampaignPage * campaignsPerPage)
                .map(campaign => (
                <button
                  key={campaign.campaign_id}
                  onClick={() => {
                    setSelectedCampaignForDisplay(campaign.campaign_id);
                    setCurrentLeadsPage(1); // Reset leads pagination when selecting new campaign
                  }}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedCampaignForDisplay === campaign.campaign_id
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 truncate">
                        {campaign.campaign_name}
                      </h5>
                      <p className="text-sm text-gray-600 mt-1">
                        {campaign.call_count} pending calls
                      </p>
                    </div>
                    <div className="ml-2">
                      <Phone className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {campaignsWithCalls.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Phone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No campaigns with pending follow-up calls found.</p>
              </div>
            )}
          </div>

          {/* Selected Campaign Leads */}
          {selectedCampaignForDisplay && (
            <div className="bg-white rounded-lg shadow border overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-semibold text-gray-900 flex items-center">
                    <Phone className="w-5 h-5 mr-2 text-orange-600" />
                    {campaignsWithCalls.find(c => c.campaign_id === selectedCampaignForDisplay)?.campaign_name} 
                    ({followUpCalls.filter(call => call.campaign_id === selectedCampaignForDisplay).length} leads)
                  </h4>
                  <button
                    onClick={() => {
                      setNewActivation({
                        ...newActivation,
                        campaign_id: selectedCampaignForDisplay,
                        campaign_name: campaignsWithCalls.find(c => c.campaign_id === selectedCampaignForDisplay)?.campaign_name
                      });
                      setShowCampaignActivationModal(true);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center"
                  >
                    <Play className="w-3 h-3 mr-1" />
                    Activate Follow-up
                  </button>
                </div>
              </div>
              
              {(() => {
                const campaignCalls = followUpCalls.filter(call => call.campaign_id === selectedCampaignForDisplay);
                const totalLeads = campaignCalls.length;
                const totalPages = Math.ceil(totalLeads / leadsPerPage);
                const paginatedCalls = campaignCalls.slice((currentLeadsPage - 1) * leadsPerPage, currentLeadsPage * leadsPerPage);
                
                return (
                  <>
                    {/* Leads Pagination */}
                    {totalLeads > leadsPerPage && (
                      <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => setCurrentLeadsPage(Math.max(1, currentLeadsPage - 1))}
                            disabled={currentLeadsPage === 1}
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              currentLeadsPage === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                          >
                            Previous
                          </button>
                          
                          <div className="text-sm text-gray-700">
                            Page {currentLeadsPage} of {totalPages} ({totalLeads} leads)
                          </div>
                          
                          <button
                            onClick={() => setCurrentLeadsPage(Math.min(totalPages, currentLeadsPage + 1))}
                            disabled={currentLeadsPage >= totalPages}
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              currentLeadsPage >= totalPages
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Leads List */}
                    <div className="divide-y divide-gray-200">
                      {paginatedCalls.map(call => (
                        <div key={call.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <p className="font-medium text-gray-900">{call.lead_email}</p>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(call.priority)}`}>
                                  {call.priority}
                                </span>
                              </div>
                              <div className="mt-1 text-sm text-gray-600">
                                <p>Opened: {formatDate(call.open_event_date)}</p>
                                <p>Original Inbox: {call.original_inbox_email}</p>
                                {call.notes && <p>Notes: {call.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                                Call
                              </button>
                              <button className="text-gray-600 hover:text-gray-800 font-medium text-sm">
                                Notes
                              </button>
                              {canDelete() && (
                                <button
                                  onClick={() => deleteFollowUpLead(call.id, call.lead_email)}
                                  disabled={deletingLead === call.id}
                                  className="text-red-600 hover:text-red-800 disabled:text-red-400 font-medium text-sm flex items-center"
                                >
                                  {deletingLead === call.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3 h-3" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
            <button 
              onClick={handleCreateTemplate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map(template => (
              <div key={template.id} className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900">{template.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Sequence Step: {template.sequence_step || 1}</p>
                  <p>Delay: {Math.round((template.delay_minutes || 10080) / 1440)} days</p>
                  {template.is_sequence_template && (
                    <p className="text-blue-600">Sequence Template</p>
                  )}
                </div>
                <div className="mt-4 flex items-center space-x-2">
                  <button 
                    onClick={() => handleEditTemplate(template)}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </button>
                  <button 
                    onClick={() => handlePreviewTemplate(template)}
                    className="text-purple-600 hover:text-purple-800 font-medium text-sm flex items-center"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </button>
                  {canDelete() && (
                    <button 
                      onClick={() => deleteTemplate(template.id)}
                      className="text-red-600 hover:text-red-800 font-medium text-sm flex items-center"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Pending Tab */}
      {activeTab === 'todays-pending' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Timer className="w-5 h-5 mr-2 text-blue-600" />
                Today's Pending Executions
              </h3>
              <p className="text-sm text-gray-500 mt-1">Upcoming automation emails scheduled to send today</p>
            </div>
            <button
              onClick={() => loadTodaysPending(1)}
              disabled={todaysPendingLoading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium flex items-center text-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${todaysPendingLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-amber-50 p-4 rounded-lg shadow-sm border border-amber-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Pending Today</p>
                  <p className="text-xl font-bold text-amber-600">{todaysPendingTotal}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Total Emails Sent</p>
                  <p className="text-xl font-bold text-blue-600">{sequenceAnalytics?.total_emails_sent || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Active Sequences</p>
                  <p className="text-xl font-bold text-green-600">{sequenceAnalytics?.active_sequences || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Today's Pending Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-amber-600" />
                Pending Executions for Today ({todaysPendingTotal})
              </h4>
            </div>
            {todaysPendingLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : todaysPending.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {todaysPending.map(exec => (
                        <tr key={exec.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{exec.lead_email}</div>
                            {exec.original_inbox_email && (
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Inbox className="w-3 h-3" />
                                {exec.original_inbox_email}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {exec.email_automation_sequences?.email_automation_campaign_activation?.campaign_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Step {exec.sequence_step || 1}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                            {exec.email_subject || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {exec.scheduled_send_time ? new Date(exec.scheduled_send_time).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {todaysPendingTotal > todaysPendingPerPage && (
                  <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((todaysPendingPage - 1) * todaysPendingPerPage) + 1} to {Math.min(todaysPendingPage * todaysPendingPerPage, todaysPendingTotal)} of {todaysPendingTotal}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => loadTodaysPending(todaysPendingPage - 1)}
                        disabled={todaysPendingPage === 1}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          todaysPendingPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.ceil(todaysPendingTotal / todaysPendingPerPage) }, (_, i) => i + 1)
                        .filter(page => page === 1 || page === Math.ceil(todaysPendingTotal / todaysPendingPerPage) || Math.abs(page - todaysPendingPage) <= 1)
                        .map((page, idx, arr) => (
                          <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && <span className="px-2 text-gray-400">...</span>}
                            <button
                              onClick={() => loadTodaysPending(page)}
                              className={`px-3 py-1 rounded-md text-sm font-medium ${
                                todaysPendingPage === page ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                      <button
                        onClick={() => loadTodaysPending(todaysPendingPage + 1)}
                        disabled={todaysPendingPage >= Math.ceil(todaysPendingTotal / todaysPendingPerPage)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          todaysPendingPage >= Math.ceil(todaysPendingTotal / todaysPendingPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Timer className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium">No pending executions for today</p>
                <p className="text-xs mt-1">All scheduled emails for today have been sent or none are scheduled</p>
              </div>
            )}
          </div>

          {/* Execution Search Section */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 flex items-center">
                <Search className="w-5 h-5 mr-2 text-blue-600" />
                Search All Executions
              </h4>
              <p className="text-xs text-gray-500 mt-1">Search across all executions in the database by lead email</p>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by lead email..."
                    value={executionSearchQuery}
                    onChange={(e) => setExecutionSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') searchExecutions(executionSearchQuery, 1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <button
                  onClick={() => searchExecutions(executionSearchQuery, 1)}
                  disabled={executionSearchLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium flex items-center text-sm"
                >
                  {executionSearchLoading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  Search
                </button>
                {executionSearchActive && (
                  <button
                    onClick={clearExecutionSearch}
                    className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Search Results */}
            {executionSearchActive && (
              <div>
                {executionSearchLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">Searching...</span>
                  </div>
                ) : executionSearchResults.length > 0 ? (
                  <>
                    <div className="px-6 py-2 bg-gray-50 border-t border-b border-gray-200 text-sm text-gray-600">
                      Found {executionSearchTotal} execution{executionSearchTotal !== 1 ? 's' : ''} matching "{executionSearchQuery}"
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Executed</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {executionSearchResults.map(exec => (
                            <tr key={exec.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{exec.lead_email}</div>
                                {exec.original_inbox_email && (
                                  <div className="text-xs text-gray-500 flex items-center gap-1">
                                    <Inbox className="w-3 h-3" />
                                    {exec.original_inbox_email}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {exec.email_automation_sequences?.email_automation_campaign_activation?.campaign_name || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Step {exec.sequence_step || 1}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                {exec.email_subject || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  exec.status === 'sent' ? 'bg-green-100 text-green-800' :
                                  exec.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                  exec.status === 'failed' ? 'bg-red-100 text-red-800' :
                                  exec.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {exec.status === 'sent' && <CheckCircle className="w-3 h-3 mr-1" />}
                                  {exec.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                                  {exec.status === 'failed' && <AlertCircle className="w-3 h-3 mr-1" />}
                                  {exec.status === 'cancelled' && <X className="w-3 h-3 mr-1" />}
                                  {exec.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {exec.scheduled_send_time ? formatDate(exec.scheduled_send_time) : exec.scheduled_at ? formatDate(exec.scheduled_at) : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {exec.executed_at ? formatDate(exec.executed_at) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Search Pagination */}
                    {executionSearchTotal > executionSearchPerPage && (
                      <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing {((executionSearchPage - 1) * executionSearchPerPage) + 1} to {Math.min(executionSearchPage * executionSearchPerPage, executionSearchTotal)} of {executionSearchTotal}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => searchExecutions(executionSearchQuery, executionSearchPage - 1)}
                            disabled={executionSearchPage === 1}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              executionSearchPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                          >
                            Previous
                          </button>
                          {Array.from({ length: Math.ceil(executionSearchTotal / executionSearchPerPage) }, (_, i) => i + 1)
                            .filter(page => page === 1 || page === Math.ceil(executionSearchTotal / executionSearchPerPage) || Math.abs(page - executionSearchPage) <= 1)
                            .map((page, idx, arr) => (
                              <React.Fragment key={page}>
                                {idx > 0 && arr[idx - 1] !== page - 1 && <span className="px-2 text-gray-400">...</span>}
                                <button
                                  onClick={() => searchExecutions(executionSearchQuery, page)}
                                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                                    executionSearchPage === page ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            ))}
                          <button
                            onClick={() => searchExecutions(executionSearchQuery, executionSearchPage + 1)}
                            disabled={executionSearchPage >= Math.ceil(executionSearchTotal / executionSearchPerPage)}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              executionSearchPage >= Math.ceil(executionSearchTotal / executionSearchPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500 border-t border-gray-200">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No executions found for "{executionSearchQuery}"</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Advanced Automation Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* System Settings */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h4 className="text-md font-semibold text-gray-900 mb-4">System Settings</h4>
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings?.sequence_management_enabled || false}
                    onChange={(e) => updateSettings({ sequence_management_enabled: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable Sequence Management</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings?.duplicate_protection_enabled || false}
                    onChange={(e) => updateSettings({ duplicate_protection_enabled: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable Duplicate Protection</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings?.time_window_enabled || false}
                    onChange={(e) => updateSettings({ time_window_enabled: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable Time Window Management</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings?.followup_call_enabled || false}
                    onChange={(e) => updateSettings({ followup_call_enabled: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable Follow-up Call Lists</span>
                </label>
              </div>
            </div>
            
            {/* Migration Section */}
            <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
              <h4 className="text-md font-semibold text-yellow-800 mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                Migrate Existing Leads
              </h4>
              <div className="space-y-4">
                <p className="text-sm text-yellow-700">
                  One-time migration to add existing opened leads (without replies) to the automation system.
                  Active campaigns will create sequences, inactive campaigns will add to follow-up calls.
                </p>
                <button
                  onClick={runExistingLeadsMigration}
                  disabled={migrationRunning}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white px-4 py-2 rounded-lg font-medium flex items-center"
                >
                  {migrationRunning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Run Migration
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Time Window Settings */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Default Time Window</h4>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={settings?.default_time_window?.start_time || '01:00'}
                      onChange={(e) => updateSettings({ 
                        default_time_window: {
                          ...settings?.default_time_window,
                          start_time: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={settings?.default_time_window?.end_time || '09:00'}
                      onChange={(e) => updateSettings({ 
                        default_time_window: {
                          ...settings?.default_time_window,
                          end_time: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Delay (minutes)</label>
                    <input
                      type="number"
                      value={settings?.default_time_window?.min_delay_minutes || 1}
                      onChange={(e) => updateSettings({ 
                        default_time_window: {
                          ...settings?.default_time_window,
                          min_delay_minutes: parseInt(e.target.value) || 1
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Delay (minutes)</label>
                    <input
                      type="number"
                      value={settings?.default_time_window?.max_delay_minutes || 10}
                      onChange={(e) => updateSettings({ 
                        default_time_window: {
                          ...settings?.default_time_window,
                          max_delay_minutes: parseInt(e.target.value) || 10
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings?.default_time_window?.randomize_within_window || false}
                    onChange={(e) => updateSettings({ 
                      default_time_window: {
                        ...settings?.default_time_window,
                        randomize_within_window: e.target.checked
                      }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Randomize within window</span>
                </label>
              </div>
            </div>

            {/* Daily Limit Settings */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                Daily Send Limit
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Emails Per Day
                  </label>
                  <input
                    type="number"
                    value={settings?.max_daily_sends || 100}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value) || 100;
                      setSettings({ ...settings, max_daily_sends: newValue });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="1000"
                    placeholder="100"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum number of emails to schedule per day. Overflow will be scheduled on subsequent days.
                  </p>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Current Usage (Today)</p>
                    <p className="text-xs text-gray-500">Scheduled emails for today</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">
                      {settings?.current_daily_sends || 0}
                    </p>
                    <p className="text-xs text-gray-500">
                      of {settings?.max_daily_sends || 100}
                    </p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.min(100, ((settings?.current_daily_sends || 0) / (settings?.max_daily_sends || 100)) * 100)}%` 
                    }}
                  />
                </div>
                <button
                  onClick={async () => {
                    try {
                      const { data, error } = await supabase.rpc('update_automation_settings', {
                        p_max_daily_sends: settings?.max_daily_sends || 100,
                        p_time_window_enabled: settings?.time_window_enabled || false,
                        p_default_time_window: settings?.default_time_window || null
                      });
                      
                      if (error) throw error;
                      
                      alert('✅ Daily limit updated successfully!');
                      await loadSettings();
                    } catch (error) {
                      logger.error('Error updating daily limit:', error);
                      alert('❌ Error updating daily limit: ' + error.message);
                    }
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Daily Limit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Activation Modal */}
      {showCampaignActivationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Activate Campaign Follow-up</h3>
                <button
                  onClick={() => setShowCampaignActivationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Campaign Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Campaign ID</label>
                    <input
                      type="text"
                      value={newActivation.campaign_id}
                      onChange={(e) => setNewActivation({ ...newActivation, campaign_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter campaign ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                    <input
                      type="text"
                      value={newActivation.campaign_name}
                      onChange={(e) => setNewActivation({ ...newActivation, campaign_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter campaign name"
                    />
                  </div>
                </div>

                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Template</label>
                  <select
                    value={newActivation.template_id}
                    onChange={(e) => setNewActivation({ ...newActivation, template_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a template (optional)</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sequence Configuration */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Sequence Configuration</h4>
                  <div className="space-y-4">
                    {newActivation.sequence_config.steps.map((step, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-medium">
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Delay (days)</label>
                          <input
                            type="number"
                            value={step.delay_days || ''}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              updateSequenceConfig(index, 'delay_days', isNaN(value) ? 1 : value);
                            }}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            min="1"
                            max="365"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                          <select
                            value={step.template_id || ''}
                            onChange={(e) => updateSequenceConfig(index, 'template_id', e.target.value || null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Use default template</option>
                            {templates.map(template => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Time Window Configuration */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Sending Time Window</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={newActivation.sending_time_window.start_time}
                        onChange={(e) => setNewActivation({
                          ...newActivation,
                          sending_time_window: {
                            ...newActivation.sending_time_window,
                            start_time: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      <input
                        type="time"
                        value={newActivation.sending_time_window.end_time}
                        onChange={(e) => setNewActivation({
                          ...newActivation,
                          sending_time_window: {
                            ...newActivation.sending_time_window,
                            end_time: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Min Delay</label>
                      <input
                        type="number"
                        value={newActivation.sending_time_window.min_delay_minutes}
                        onChange={(e) => setNewActivation({
                          ...newActivation,
                          sending_time_window: {
                            ...newActivation.sending_time_window,
                            min_delay_minutes: parseInt(e.target.value) || 1
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Delay</label>
                      <input
                        type="number"
                        value={newActivation.sending_time_window.max_delay_minutes}
                        onChange={(e) => setNewActivation({
                          ...newActivation,
                          sending_time_window: {
                            ...newActivation.sending_time_window,
                            max_delay_minutes: parseInt(e.target.value) || 10
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newActivation.sending_time_window.randomize_within_window}
                        onChange={(e) => setNewActivation({
                          ...newActivation,
                          sending_time_window: {
                            ...newActivation.sending_time_window,
                            randomize_within_window: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Randomize sending time within window</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={() => setShowCampaignActivationModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={activateCampaign}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Activate Campaign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h3>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => {
                      const sanitizedValue = e.target.value.replace(/<[^>]*>/g, ''); // Basic HTML tag removal
                      setNewTemplate({ ...newTemplate, name: sanitizedValue });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter template name"
                    maxLength={100}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newTemplate.description}
                    onChange={(e) => {
                      const sanitizedValue = e.target.value.replace(/<script[^>]*>.*?<\/script>/gi, '').replace(/<[^>]*>/g, '');
                      setNewTemplate({ ...newTemplate, description: sanitizedValue });
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter template description"
                    maxLength={500}
                  />
                </div>

                {/* Trigger Event */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Event</label>
                  <select
                    value={newTemplate.trigger_event}
                    onChange={(e) => setNewTemplate({ ...newTemplate, trigger_event: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="open">Email Open</option>
                    <option value="click">Link Click</option>
                    <option value="reply">Email Reply</option>
                  </select>
                </div>

                {/* Subject Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject Template</label>
                  <input
                    type="text"
                    value={newTemplate.subject_template}
                    onChange={(e) => {
                      const sanitizedValue = e.target.value.replace(/<script[^>]*>.*?<\/script>/gi, '').replace(/javascript:/gi, '');
                      setNewTemplate({ ...newTemplate, subject_template: sanitizedValue });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email subject (use {{first_name}}, {{company}}, etc.)"
                    maxLength={200}
                  />
                  {/* Variable Detection Feedback */}
                  {newTemplate.subject_template && (
                    <div className="mt-2 text-sm">
                      {(() => {
                        const variables = newTemplate.subject_template.match(/{{\s*\w+\s*}}/g) || [];
                        const validVariables = ['{{first_name}}', '{{last_name}}', '{{company}}', '{{campaign_name}}', '{{email}}', '{{source}}', '{{lead_score}}', '{{current_date}}'];
                        const foundValid = variables.filter(v => validVariables.includes(v.replace(/\s/g, '')));
                        const foundInvalid = variables.filter(v => !validVariables.includes(v.replace(/\s/g, '')));
                        
                        return (
                          <div className="space-y-1">
                            {foundValid.length > 0 && (
                              <div className="text-green-600">
                                ✅ Valid variables: {foundValid.join(', ')}
                              </div>
                            )}
                            {foundInvalid.length > 0 && (
                              <div className="text-red-600">
                                ❌ Invalid variables: {foundInvalid.join(', ')}
                              </div>
                            )}
                          </div>
                        );
                      })()
                      }
                    </div>
                  )}
                </div>

                {/* Content Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Content</label>
                  <textarea
                    value={newTemplate.content_template}
                    onChange={(e) => {
                      const sanitizedValue = e.target.value
                        .replace(/<script[^>]*>.*?<\/script>/gi, '')
                        .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
                        .replace(/javascript:/gi, '')
                        .replace(/on\w+\s*=/gi, '');
                      setNewTemplate({ ...newTemplate, content_template: sanitizedValue });
                    }}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email content (use {{first_name}}, {{company}}, {{campaign_name}}, etc.)"
                    maxLength={30000}
                  />
                  {/* Content Statistics and Validation */}
                  <div className="mt-2 flex justify-between items-start text-sm">
                    <div className="text-gray-600">
                      Characters: {newTemplate.content_template?.length || 0}/30000
                    </div>
                    {newTemplate.content_template && (
                      <div className="text-right">
                        {(() => {
                          const variables = newTemplate.content_template.match(/{{\s*\w+\s*}}/g) || [];
                          const validVariables = ['{{first_name}}', '{{last_name}}', '{{company}}', '{{campaign_name}}', '{{email}}', '{{source}}', '{{lead_score}}', '{{current_date}}'];
                          const foundValid = variables.filter(v => validVariables.includes(v.replace(/\s/g, '')));
                          const foundInvalid = variables.filter(v => !validVariables.includes(v.replace(/\s/g, '')));
                          
                          return (
                            <div className="space-y-1">
                              {foundValid.length > 0 && (
                                <div className="text-green-600 text-xs">
                                  ✅ {foundValid.length} valid variables found
                                </div>
                              )}
                              {foundInvalid.length > 0 && (
                                <div className="text-red-600 text-xs">
                                  ❌ {foundInvalid.length} invalid variables: {foundInvalid.join(', ')}
                                </div>
                              )}
                            </div>
                          );
                        })()
                        }
                      </div>
                    )}
                  </div>
                </div>

                {/* Template Settings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Delay Days</label>
                    <input
                      type="number"
                      value={Math.round((newTemplate.delay_minutes || 10080) / 1440)} // Convert minutes to days for display
                      onChange={(e) => {
                        const days = parseInt(e.target.value) || 1;
                        const minutes = days * 1440; // Convert days to minutes
                        setNewTemplate({ ...newTemplate, delay_minutes: minutes });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                      max="365"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sequence Step</label>
                    <input
                      type="number"
                      value={newTemplate.sequence_step}
                      onChange={(e) => setNewTemplate({ ...newTemplate, sequence_step: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                      max="5"
                    />
                  </div>
                  <div className="flex items-center space-x-4 pt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newTemplate.is_active}
                        onChange={(e) => setNewTemplate({ ...newTemplate, is_active: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newTemplate.is_sequence_template}
                        onChange={(e) => setNewTemplate({ ...newTemplate, is_sequence_template: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Sequence Template</span>
                    </label>
                  </div>
                </div>

                {/* Variable Helper */}
                 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                   <h4 className="text-sm font-medium text-blue-800 mb-2">Available Variables:</h4>
                   <div className="text-sm text-blue-700 space-y-1">
                     <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                       <code>{'{{first_name}}'}</code>
                       <code>{'{{last_name}}'}</code>
                       <code>{'{{company}}'}</code>
                       <code>{'{{campaign_name}}'}</code>
                       <code>{'{{email}}'}</code>
                       <code>{'{{source}}'}</code>
                       <code>{'{{lead_score}}'}</code>
                       <code>{'{{current_date}}'}</code>
                     </div>
                   </div>
                 </div>
              </div>
              
              <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTemplate}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingTemplate ? 'Update' : 'Create'} Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showPreviewModal && previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  {previewTemplate.name}
                </h3>
                <div className="flex items-center space-x-1 bg-white rounded-lg p-1 shadow-sm">
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`p-2 rounded-md transition-all ${previewDevice === 'desktop' ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    title="Desktop View"
                  >
                    <Monitor className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('tablet')}
                    className={`p-2 rounded-md transition-all ${previewDevice === 'tablet' ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    title="Tablet View"
                  >
                    <Tablet className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`p-2 rounded-md transition-all ${previewDevice === 'mobile' ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    title="Mobile View"
                  >
                    <Smartphone className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => sendTestEmail(previewTemplate)}
                  disabled={sendingTestEmail}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-medium flex items-center transition-all"
                  title="Send test email to aiman@otherwiselight.com"
                >
                  {sendingTestEmail ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {sendingTestEmail ? 'Sending...' : 'Test Email'}
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="flex h-[calc(95vh-140px)]">
              {/* Left Panel - Template Info & Controls */}
              <div className="w-1/3 p-4 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                {/* Template Details */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-blue-600" />
                    Template Details
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</div>
                      <div className="text-sm font-medium text-gray-900 mt-1">{previewTemplate.name}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Delay</div>
                        <div className="text-sm font-medium text-gray-900 mt-1">{Math.round((previewTemplate.delay_minutes || 10080) / 1440)} days</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Step</div>
                        <div className="text-sm font-medium text-gray-900 mt-1">{previewTemplate.sequence_step || 1}</div>
                      </div>
                    </div>
                    {previewTemplate.description && (
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</div>
                        <div className="text-sm text-gray-700 mt-1">{previewTemplate.description}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sample Data */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-green-600" />
                    Sample Data
                  </h4>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-medium text-gray-900">Matti Virtanen</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Company:</span>
                        <span className="font-medium text-gray-900">Rakennusliike Virtanen Oy</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Campaign:</span>
                        <span className="font-medium text-gray-900">Valaistusratkaisut 2024</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Test Email:</span>
                        <span className="font-medium text-blue-600">aiman@otherwiselight.com</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Template Variables */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <Code className="w-4 h-4 mr-2 text-purple-600" />
                    Available Variables
                  </h4>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="grid grid-cols-1 gap-1 text-xs">
                      <code className="text-purple-600">{'{{first_name}}'}</code>
                      <code className="text-purple-600">{'{{last_name}}'}</code>
                      <code className="text-purple-600">{'{{company}}'}</code>
                      <code className="text-purple-600">{'{{campaign_name}}'}</code>
                      <code className="text-purple-600">{'{{email}}'}</code>
                      <code className="text-purple-600">{'{{source}}'}</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Email Preview */}
              <div className="flex-1 p-4 overflow-y-auto">
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-2 flex items-center">
                    <Eye className="w-4 h-4 mr-2 text-blue-600" />
                    Email Preview ({previewDevice})
                  </h4>
                </div>
              
                <div className={`border border-gray-300 rounded-lg overflow-hidden shadow-lg ${
                  previewDevice === 'mobile' ? 'max-w-sm mx-auto' :
                  previewDevice === 'tablet' ? 'max-w-md mx-auto' :
                  'w-full'
                }`}>
                  {/* Email Client Header */}
                  <div className="bg-gradient-to-r from-gray-100 to-gray-200 px-4 py-3 border-b border-gray-300">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm">
                          O
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Otherwise Light</div>
                          <div className="text-gray-600 text-xs">jani@otherwiselight.org → aiman@otherwiselight.com</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-gray-500 text-xs">{new Date().toLocaleDateString()}</div>
                        <div className="text-green-600 text-xs font-medium">✓ Test Email</div>
                      </div>
                    </div>
                    <div className="mt-3 font-semibold text-gray-900 text-base">
                      {getPreviewSubject(previewTemplate)}
                    </div>
                  </div>
                  
                  {/* Email Content */}
                  <div className="bg-white p-6 min-h-[400px] max-h-[600px] overflow-y-auto">
                    {previewTemplate.content_template && previewTemplate.content_template.includes('<') ? (
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(processPreviewContent(previewTemplate), { ADD_TAGS: ['style'] })
                        }}
                      />
                    ) : (
                      <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                        {processPreviewContent(previewTemplate)}
                      </div>
                    )}
                  </div>
                  
                  {/* Email Footer */}
                  <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 text-xs text-gray-500">
                    <div className="flex justify-between items-center">
                      <span>Template: {previewTemplate.name}</span>
                      <span>Step {previewTemplate.sequence_step || 1} • {Math.round((previewTemplate.delay_minutes || 10080) / 1440)} days delay</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Test emails sent to: aiman@otherwiselight.com</span>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    handleEditTemplate(previewTemplate);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center transition-colors"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sequence Configuration Modal */}
      {showSequenceConfigModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modern Header */}
            <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white tracking-tight">
                  Campaign Configuration
                </h3>
                <p className="text-blue-200 text-sm mt-0.5">{selectedCampaign.campaign_name}</p>
              </div>
              <button
                onClick={() => setShowSequenceConfigModal(false)}
                className="text-white/70 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

              {/* Campaign Info Card */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50/40 rounded-xl border border-slate-200/80 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Target className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Campaign ID</p>
                      <p className="text-slate-900 font-semibold text-xs">{selectedCampaign.campaign_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Template</p>
                      <p className="text-slate-900 font-semibold text-xs">{selectedCampaign.email_automation_templates?.name || 'Default'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Status</p>
                      <p className="text-green-600 font-bold text-xs">Active</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sequence Steps */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center">
                    <Layers className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 tracking-tight">Sequence Steps</h4>
                </div>
                <div className="space-y-3">
                  {selectedCampaign.sequence_config?.steps?.map((step, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-col items-center">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-md">
                          {step.step}
                        </div>
                        {index < (selectedCampaign.sequence_config?.steps?.length || 0) - 1 && (
                          <div className="w-0.5 h-6 bg-gradient-to-b from-blue-300 to-transparent mt-1" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Delay (days)</label>
                            <input
                              type="number"
                              value={step.delay_days}
                              onChange={(e) => {
                                const updatedSteps = [...selectedCampaign.sequence_config.steps];
                                updatedSteps[index] = { ...updatedSteps[index], delay_days: parseInt(e.target.value) || 1 };
                                setSelectedCampaign({
                                  ...selectedCampaign,
                                  sequence_config: {
                                    ...selectedCampaign.sequence_config,
                                    steps: updatedSteps
                                  }
                                });
                              }}
                              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                              min="1"
                              max="365"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Template</label>
                            <select
                              value={step.template_id || ''}
                              onChange={(e) => {
                                const updatedSteps = [...selectedCampaign.sequence_config.steps];
                                updatedSteps[index] = { ...updatedSteps[index], template_id: e.target.value || null };
                                setSelectedCampaign({
                                  ...selectedCampaign,
                                  sequence_config: {
                                    ...selectedCampaign.sequence_config,
                                    steps: updatedSteps
                                  }
                                });
                              }}
                              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                            >
                              <option value="">Use default template</option>
                              {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )) || []}
                </div>
              </div>

              {/* Overdue & Auto-Stopped Leads Per Step */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-md flex items-center justify-center">
                      <AlertCircle className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 tracking-tight">Overdue & Auto-Stopped Leads</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    {campaignStepLoading && (
                      <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
                    )}
                    <button
                      onClick={() => loadCampaignStepData(selectedCampaign.campaign_id)}
                      className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg p-1.5 transition-all"
                      title="Refresh"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {campaignStepData && (campaignStepData.totalOverdue > 0 || campaignStepData.totalStopped > 0) ? (
                  <div className="space-y-3">
                    {/* Campaign-level summary */}
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200/60">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 bg-orange-100 px-2.5 py-1 rounded-lg">
                          <Clock className="w-3 h-3" />
                          {campaignStepData.totalOverdue} overdue
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-100 px-2.5 py-1 rounded-lg">
                          <Pause className="w-3 h-3" />
                          {campaignStepData.totalStopped} stopped
                        </span>
                      </div>
                      <button
                        onClick={() => handleRescheduleCampaign(selectedCampaign.campaign_id, 'all')}
                        disabled={reschedulingCampaign}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-blue-400 disabled:to-indigo-400 text-white px-4 py-1.5 rounded-lg text-xs font-bold flex items-center shadow-sm transition-all"
                      >
                        {reschedulingCampaign ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <Repeat className="w-3 h-3 mr-1.5" />}
                        Reschedule All
                      </button>
                    </div>

                    {/* Per-step controls */}
                    {selectedCampaign.sequence_config?.steps?.map((step) => {
                      const stepInfo = campaignStepData.stepData?.[step.step];
                      const overdueCount = stepInfo?.overdue?.length || 0;
                      const stoppedCount = stepInfo?.stopped?.length || 0;
                      if (overdueCount === 0 && stoppedCount === 0) return null;
                      const stepSettings = stepRescheduleSettings[step.step] || {};
                      const isReschedulingThis = reschedulingStep === step.step;
                      return (
                        <div key={step.step} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                          {/* Step header */}
                          <div className="flex items-center justify-between px-4 py-3 bg-slate-50/80 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-sm">
                                {step.step}
                              </div>
                              <span className="text-sm font-bold text-slate-800">Step {step.step}</span>
                              {overdueCount > 0 && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-lg font-bold">
                                  {overdueCount} overdue
                                </span>
                              )}
                              {stoppedCount > 0 && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-lg font-bold">
                                  {stoppedCount} stopped
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-4 space-y-3">
                            {/* Lead emails preview */}
                            <div className="space-y-1">
                              {(stepInfo?.overdue || []).slice(0, 3).map(seq => (
                                <div key={seq.id} className="text-xs text-orange-600 flex items-center gap-1.5 py-0.5">
                                  <Clock className="w-3 h-3 flex-shrink-0" />
                                  <span className="font-medium">{seq.lead_email}</span>
                                </div>
                              ))}
                              {(stepInfo?.stopped || []).slice(0, 3).map(seq => (
                                <div key={seq.id} className="text-xs text-red-600 flex items-center gap-1.5 py-0.5">
                                  <Pause className="w-3 h-3 flex-shrink-0" />
                                  <span className="font-medium">{seq.lead_email}</span>
                                  <span className="text-red-400">({seq.status})</span>
                                </div>
                              ))}
                              {(overdueCount + stoppedCount) > 6 && (
                                <p className="text-xs text-slate-400 italic pl-4">
                                  ...and {(overdueCount + stoppedCount) - 6} more
                                </p>
                              )}
                            </div>

                            {/* Reschedule controls */}
                            <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-100 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Date</label>
                                  <input
                                    type="date"
                                    value={stepSettings.scheduledDate || ''}
                                    onChange={(e) => updateStepSetting(step.step, 'scheduledDate', e.target.value)}
                                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Time</label>
                                  <input
                                    type="time"
                                    value={stepSettings.scheduledTime || ''}
                                    onChange={(e) => updateStepSetting(step.step, 'scheduledTime', e.target.value)}
                                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Template</label>
                                  <select
                                    value={stepSettings.templateId || ''}
                                    onChange={(e) => updateStepSetting(step.step, 'templateId', e.target.value || null)}
                                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                  >
                                    <option value="">Keep current template</option>
                                    {templates.map(t => (
                                      <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              {(!stepSettings.scheduledDate || !stepSettings.scheduledTime) && (
                                <p className="text-xs text-slate-400 italic">Leave date/time empty to use campaign time window rules</p>
                              )}
                              <div className="flex flex-wrap items-center gap-2 pt-1">
                                {overdueCount > 0 && (
                                  <button
                                    onClick={() => handleRescheduleStep(selectedCampaign.campaign_id, step.step, 'overdue')}
                                    disabled={isReschedulingThis}
                                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-orange-300 disabled:to-orange-400 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center shadow-sm transition-all"
                                  >
                                    {isReschedulingThis ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <Repeat className="w-3 h-3 mr-1.5" />}
                                    Reschedule {overdueCount} Overdue
                                  </button>
                                )}
                                {stoppedCount > 0 && (
                                  <button
                                    onClick={() => handleRescheduleStep(selectedCampaign.campaign_id, step.step, 'stopped')}
                                    disabled={isReschedulingThis}
                                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-red-300 disabled:to-red-400 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center shadow-sm transition-all"
                                  >
                                    {isReschedulingThis ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <Repeat className="w-3 h-3 mr-1.5" />}
                                    Reschedule {stoppedCount} Stopped
                                  </button>
                                )}
                                {overdueCount > 0 && stoppedCount > 0 && (
                                  <button
                                    onClick={() => handleRescheduleStep(selectedCampaign.campaign_id, step.step, 'all')}
                                    disabled={isReschedulingThis}
                                    className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-blue-300 disabled:to-indigo-400 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center shadow-sm transition-all"
                                  >
                                    {isReschedulingThis ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <Repeat className="w-3 h-3 mr-1.5" />}
                                    Reschedule All Step {step.step}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-5 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200/60 text-center">
                    <CheckCircle className="w-7 h-7 text-emerald-500 mx-auto mb-1.5" />
                    <p className="text-sm text-emerald-800 font-bold">No overdue or auto-stopped leads</p>
                    <p className="text-xs text-emerald-600 mt-0.5">All sequences are running on schedule</p>
                  </div>
                )}
              </div>

              {/* Time Window Configuration */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 rounded-md flex items-center justify-center">
                    <Clock className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 tracking-tight">Sending Time Window</h4>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Start Time</label>
                      <input
                        type="time"
                        value={selectedCampaign.sending_time_window?.start_time || '01:00'}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          sending_time_window: {
                            ...selectedCampaign.sending_time_window,
                            start_time: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">End Time</label>
                      <input
                        type="time"
                        value={selectedCampaign.sending_time_window?.end_time || '09:00'}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          sending_time_window: {
                            ...selectedCampaign.sending_time_window,
                            end_time: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Min Delay (min)</label>
                      <input
                        type="number"
                        value={selectedCampaign.sending_time_window?.min_delay_minutes || 1}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          sending_time_window: {
                            ...selectedCampaign.sending_time_window,
                            min_delay_minutes: parseInt(e.target.value) || 1
                          }
                        })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Max Delay (min)</label>
                      <input
                        type="number"
                        value={selectedCampaign.sending_time_window?.max_delay_minutes || 10}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          sending_time_window: {
                            ...selectedCampaign.sending_time_window,
                            max_delay_minutes: parseInt(e.target.value) || 1
                          }
                        })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                        min="1"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedCampaign.sending_time_window?.randomize_within_window || false}
                      onChange={(e) => setSelectedCampaign({
                        ...selectedCampaign,
                        sending_time_window: {
                          ...selectedCampaign.sending_time_window,
                          randomize_within_window: e.target.checked
                        }
                      })}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition-all"
                    />
                    <span className="text-sm text-slate-600 font-medium group-hover:text-slate-800 transition-colors">Randomize sending time within window</span>
                  </label>
                </div>
              </div>

              {/* Sequence Settings */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-md flex items-center justify-center">
                    <Settings className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 tracking-tight">Sequence Settings</h4>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Max Sequences</label>
                      <input
                        type="number"
                        value={selectedCampaign.sequence_config?.max_sequences || 5}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          sequence_config: {
                            ...selectedCampaign.sequence_config,
                            max_sequences: parseInt(e.target.value) || 1
                          }
                        })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                        min="1"
                        max="10"
                      />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={selectedCampaign.sequence_config?.stop_on_reply || false}
                          onChange={(e) => setSelectedCampaign({
                            ...selectedCampaign,
                            sequence_config: {
                              ...selectedCampaign.sequence_config,
                              stop_on_reply: e.target.checked
                            }
                          })}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition-all"
                        />
                        <span className="text-sm text-slate-600 font-medium group-hover:text-slate-800 transition-colors">Stop sequence on reply</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowSequenceConfigModal(false)}
                className="px-5 py-2.5 text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 rounded-xl font-semibold text-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    // Use the new RPC endpoint for proper reconfiguration
                    const success = await updateCampaignConfiguration(
                      selectedCampaign.campaign_id,
                      selectedCampaign.sequence_config,
                      selectedCampaign.sending_time_window
                    );
                    
                    if (success) {
                      setShowSequenceConfigModal(false);
                    }
                  } catch (error) {
                    logger.error('Error saving campaign configuration:', error);
                    alert('Error saving configuration: ' + error.message);
                  }
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold text-sm flex items-center shadow-md hover:shadow-lg transition-all"
              >
                <Save className="w-4 h-4 mr-2" />
                Save & Reconfigure
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Migration Results Modal */}
      {showMigrationModal && migrationResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  Migration Complete
                </h3>
                <button
                  onClick={() => setShowMigrationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Processed:</span>
                  <span className="text-sm font-medium text-gray-900">{migrationResults.totalProcessed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Added to Sequences:</span>
                  <span className="text-sm font-medium text-green-600">{migrationResults.addedToSequences}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Added to Follow-up Calls:</span>
                  <span className="text-sm font-medium text-blue-600">{migrationResults.addedToFollowUpCalls}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Skipped (already exists):</span>
                  <span className="text-sm font-medium text-yellow-600">{migrationResults.skipped}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Errors:</span>
                  <span className="text-sm font-medium text-red-600">{migrationResults.errors}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowMigrationModal(false)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedEmailAutomationControl;